// src/constants/bookingConfig.js

export const COURT_NAME = 'Court Booking System';

/**
 * The build's version number, as shown in the site footer (see
 * components/AppFooter.jsx and LandingPage's copyright line).
 *
 * Sourced from REACT_APP_VERSION_NO in .env. Create React App inlines
 * REACT_APP_* values at BUILD time, so changing the number needs a rebuild
 * (or a dev-server restart) — editing .env with the server running does
 * nothing.
 *
 * Normalised to exactly one leading "v" here rather than at each display
 * site, so an .env holding "1.0.0" and one holding "v1.0.0" both render
 * "v1.0.0". Resolves to '' when the variable is unset — a missing REACT_APP_*
 * reaches the bundle as `undefined`, and every caller renders nothing rather
 * than a bare "v".
 */
export const APP_VERSION_NO = (() => {
  const raw = (process.env.REACT_APP_VERSION_NO || '').trim();
  if (!raw) return '';
  return /^v/i.test(raw) ? raw : `v${raw}`;
})();

// Accounts allowed to reach the admin "Config" screen (Sports & Courts,
// pricing, branding, payment details — everything that reshapes the whole
// deployment). Every other admin account signs in normally but only sees
// the day-to-day screens. Compared case-insensitively; see isConfigAdmin
// in AdminPage.jsx.
//
// NOTE: this is a UI-level restriction only, and it no longer covers the
// screens that SAVE /config (Config, Events and Packages, Promo). Those are
// limited to SUPER_ADMIN_EMAILS below. This list now gates only Equipment
// and Error Logs.
export const CONFIG_ADMIN_EMAILS = ['system@gmail.com', 'paddlegrip1@gmail.com'];

// Super-admin accounts, all with equal power: the only logins that may save
// /config (Config, Events and Packages, Promo, payment QR codes), and the ones
// exempt from the past-booking edit lock. Checked by isSuperAdmin
// (authServiceUtil.js).
//
// ENFORCED in database.rules.json on the auth token's email — keep the
// "/config" .write rule there in step with this list; a rule cannot import it.
//
// An address here is honoured on EVERY client project this build reaches.
// Sign-up must stay open (anonymous guest sign-in depends on it), so on a
// project where one of these accounts does NOT exist, anyone could register
// that email and gain these rights. Every address must therefore exist (or be
// reserved as a disabled account) in every client's Firebase Authentication.
export const SUPER_ADMIN_EMAILS = ['system@gmail.com', 'paddlegrip1@gmail.com'];

// How long an admin can sit idle before the dashboard signs them out.
// Enforced in authServiceUtil.js and quoted in two places of UI copy (the
// login card and Config -> System), which is why it lives here rather than
// beside the enforcement — the number has to be one value, not three.
//
// Whether it applies at all is the `adminIdleTimeoutEnabled` config flag
// (Config -> System); with that off, the session lasts until a manual
// logout.
export const ADMIN_IDLE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes

// Currency symbol printed in front of every amount when a club has not set
// its own (Config -> System -> Currency). The peso, because that is what every
// deployment predating the currency setting was billing in — see
// getCurrencySymbol in utils/currencyUtil.js.
export const DEFAULT_CURRENCY_SYMBOL = '₱';

// The club's own name for that currency, shown beside the symbol field so
// whoever edits it later knows which currency the symbol refers to.
export const DEFAULT_CURRENCY_DESCRIPTION = 'Philippine Peso';

/**
 * Per-court identity colours for the summary chips on the guest booking
 * screen, so two courts in the same list are told apart at a glance.
 *
 * FOUR slots, not one per court, and deliberately not cycled past the fourth.
 * These are the first four hues of the validated categorical palette, ordered
 * so that ALL PAIRS clear both the colour-blind separation floor (worst pair
 * delta-E 9.2, deutan) and the normal-vision floor (16.3) on a light surface —
 * no fifth hue from that palette clears both, so a fifth court falls back to
 * COURT_CHIP_NEUTRAL rather than to a colour a guest could confuse with
 * another court's.
 *
 * `tint` is the chip background; the label on top stays in the app's ink token
 * rather than the hue (every tint clears 12:1 against it), because the court
 * NAME is what identifies the court — the colour is a redundant second cue,
 * which is also what keeps this readable for a colour-blind guest.
 *
 * `accent` is the full-strength hue each tint is derived from, kept for any
 * future border or dot treatment.
 */
export const COURT_CHIP_COLORS = [
  { tint: '#dde9f8', accent: '#2a78d6' }, // blue
  { tint: '#fce7df', accent: '#eb6834' }, // orange
  { tint: '#dbf2ea', accent: '#1baf7a' }, // aqua
  { tint: '#e2dff1', accent: '#4a3aa7' }, // violet
];

/** Courts past the palette above — see COURT_CHIP_COLORS. */
export const COURT_CHIP_NEUTRAL = { tint: '#ececec', accent: '#6b6b6b' };

/**
 * Media query for the guest time slot screen's stacked layout, where the
 * selected-hours summary drops below the slot grid instead of sitting beside
 * it. Must match the `max-width: 880px` block in TimeSlotSelection.css. In
 * that layout the summary floats at the bottom of the screen once hours are
 * selected (TimeSlotSelection) until the guest closes it back under the grid,
 * and each court's card in it starts folded (SelectedSlotsSummary).
 */
export const TIME_SLOT_SUMMARY_STACKED_QUERY = '(max-width: 880px)';

export const TIME_SLOTS = [
  '6-7AM', '7-8AM', '8-9AM', '9-10AM', '10-11AM', '11-12PM',
  '12-1PM', '1-2PM', '2-3PM', '3-4PM', '4-5PM', '5-6PM',
  '6-7PM', '7-8PM', '8-9PM', '9-10PM', '10-11PM', '11-12AM'
];

export const COURTS = [1, 2];

// Values written to slotOverrides/{date}/{timeSlot}/{court} by the admin's
// "Manage availability" grid (AdminTimeSlotSelection.jsx -> STATUS_OPTIONS,
// persisted by AdminBooking.jsx's handleSaveGridChanges).
//
// Only the two below mean anything to a GUEST, which is why they are named
// here rather than left as loose strings across three files:
//
//   'openPlay' — the cell becomes a "Join Open Play" button
//   'closed'   — the cell is blocked, exactly like a court switched off in
//                Config -> Sports & Courts. This lets an admin take one
//                hour on one court out of service (maintenance, a private
//                event, a tournament) without closing the whole court for
//                the whole day.
//
// The remaining override values ('booked' / 'processing' / 'passed') are
// admin-side bookkeeping and are deliberately not read by the guest screen.
/**
 * The booker name on a slot the CLUB has taken off the market — what the
 * "Manage availability" grid writes when an admin sets an empty cell to
 * BOOKED (see createAdminBlockBooking in AdminBooking.jsx).
 *
 * These are real bookings, not slotOverrides strings, so the hour is actually
 * held against guests and against the checkout race check. They carry
 * `adminBlock: true` and a paymentAmount of 0 — the club is closing an hour,
 * not selling one, so it must not land in the export as revenue.
 */
export const ADMIN_BLOCK_BOOKER_NAME = 'ADMIN';

/**
 * How many characters the in-cell initials box on the "Manage availability"
 * grid accepts (see CellInitialsInput.jsx).
 *
 * Two, because the box is the same 40x36 badge that renders a guest's
 * initials via getInitials(), which always produces two letters. A third
 * character would overflow that badge on a phone, where the cell is already
 * the narrowest thing on the screen.
 */
export const MAX_CELL_INITIALS_LENGTH = 2;

/**
 * How far into the NEXT morning the booking grids run when a club trades past
 * midnight, as an hour 0-12. Used when `config.nextDayGridEndHour` is unset.
 *
 * 3 means the grid shows 12-1AM, 1-2AM and 2-3AM of the following date under
 * the selected day's own hours. 0 switches the extension off entirely and the
 * grids end at midnight, exactly as they did before.
 *
 * Those rows are REAL next-date cells: a booking made on one is written
 * against tomorrow, holds tomorrow's slot lock, and appears at the top of
 * tomorrow's own grid. The extension is a second place to see and book the
 * same hour, never a second copy of it.
 *
 * Separate from OPEN_PLAY_NEXT_DAY_END_HOUR (7) on purpose: how late a club
 * lets people BOOK and how late it will run a hosted session are different
 * questions, and a club that hosts until 6AM need not sell 5AM court time.
 */
export const DEFAULT_NEXT_DAY_GRID_END_HOUR = 3;

/** Upper bound for the Config field — past noon it stops being "overnight". */
export const MAX_NEXT_DAY_GRID_END_HOUR = 12;

export const SLOT_STATUS_CLOSED = 'closed';

/**
 * Where the admin's reason for closing an hour is kept:
 * slotCloseReasons/{date}/{timeSlot}/{court} = { reason, closedBy, closedAt }.
 * A sibling of slotOverrides rather than a field on it, because every reader
 * of slotOverrides (guest grid, checkout re-check, database rules) expects a
 * plain status string there.
 */
export const SLOT_CLOSE_REASONS_PATH = 'slotCloseReasons';

/**
 * Config key for Config -> System -> "Ask reason for closing timeslot". On by
 * default and read as `!== false` (see isCloseReasonPromptEnabled), so a
 * config saved before this setting existed keeps asking for a reason.
 */
export const CLOSE_REASON_PROMPT_CONFIG_KEY = 'adminAskCloseReasonEnabled';

/** Longest close reason the admin can type (also enforced in database.rules.json). */
export const SLOT_CLOSE_REASON_MAX_LENGTH = 200;
export const SLOT_STATUS_OPEN_PLAY = 'openPlay';

// How far into the NEXT morning an Open Play session may be scheduled, as an
// hour (0-23). An overnight session ("10PM - 6AM") is hosted from one date
// and runs past midnight, so the host modal offers the next day's early
// hours after the last hour of the selected date.
//
// 7 means the last offerable hour is the 6-7AM slot. It is a cap on the
// PICKER, not on what the club is open for: only hours that actually exist
// in Config -> Time Slots can be picked, so a club whose day ends at 11PM
// sees no next-day hours at all until it adds the small hours there.
export const OPEN_PLAY_NEXT_DAY_END_HOUR = 7;

// Peak: 4pm onwards
// Off-Peak: 6am-4pm
export const PRICE_PER_HOUR_PEAK = 300;
export const PRICE_PER_HOUR_OFF_PEAK = 200;

// Legacy support - using peak rate as default
export const PRICE_PER_HOUR = 300;

export const BOOKING_FEE = 0;

// Header navigation links shared by LandingPage's own header and the
// guest-flow header in App.jsx, so both stay visually/structurally in sync.
// `id` matches the section `id` on LandingPage that clicking the link
// should scroll to (see LandingPage.jsx's scrollTo / scrollToId handling).
export const NAV_LINKS = [
  { id: 'home', label: 'Home' },
  { id: 'rates', label: 'Rates' },
  { id: 'facilities', label: 'Facilities' },
  { id: 'open-play', label: 'Open Play' },
  { id: 'location', label: 'Location' },
  { id: 'faq', label: 'FAQ' },
];

// LEGACY — equipment rental used to be this fixed, hardcoded list (each
// item's price/inventory living in its own flat config field via
// configKey/inventoryKey). It's now an admin-managed, per-sport array at
// config.equipment (Config -> Equipment tab; see EquipmentRentalModal.jsx,
// AdminEquipment.jsx, equipmentAvailabilityUtil.js, and AdminConfig.jsx,
// none of which read this constant anymore). Kept only so
// ConfigContext.jsx's one-time migration can turn an old deployment's
// flat fields into real config.equipment entries with the same ids
// (important: existing equipmentHolds/equipmentManualUsage Firebase nodes
// are keyed by these ids, so the migration must preserve them exactly).
export const RENTAL_EQUIPMENT = [
  { id: 'paddle', label: 'Paddle', unit: 'paddle', configKey: 'paddleRentalFee', defaultPrice: 100, inventoryKey: 'paddleInventoryTotal', defaultInventory: 10 },
  { id: 'trainingBalls', label: 'Training Balls (set)', unit: 'set', configKey: 'ballRentalFee', defaultPrice: 20, inventoryKey: 'ballSetInventoryTotal', defaultInventory: 5 },
  { id: 'ballMachine', label: 'Ball Machine', unit: 'machine', configKey: 'ballMachineRentalFee', defaultPrice: 150, inventoryKey: 'ballMachineInventoryTotal', defaultInventory: 2 },
];

// Shared jassen-light color palette used across every admin dashboard
// screen (Booking, Export, Events, Config, User, Sidebar, BookingsList).
// Centralized here (per project convention) instead of each screen
// re-declaring its own near-identical `COLORS` object.
//
// Values reference the CSS custom properties the admin's Config -> Theme
// tab controls at runtime (see ConfigContext.jsx's dynamic <style> tag),
// with the original jassen palette as the fallback for the var()'s second
// argument. This keeps inline-styled admin UI in sync with the same
// color config that already drives the CSS-class-based screens.
export const ADMIN_COLORS = {
  ink: 'var(--jt-ink, #3d1220)',
  inkSoft: 'var(--jt-ink-soft, #704352)',
  inkFaint: 'var(--jt-ink-faint, #9c828b)',
  // Alias kept for the screens (Config/Export/Events/User) that were
  // originally written against a "textSecondary" name.
  textSecondary: 'var(--jt-ink-soft, #704352)',
  line: 'var(--jt-line, rgba(61, 18, 32, .16))',
  surface: 'var(--jt-surface, #ffffff)',
  // Alias kept for the screens that were originally written against
  // "cardSolid" / "deepBg" names for the same white card surface.
  cardSolid: 'var(--jt-surface, #ffffff)',
  deepBg: 'var(--jt-surface, #ffffff)',
  cardBg: 'rgba(61, 18, 32, .04)',
  brand: 'var(--jt-brand, #3d1220)',
  cyan: 'var(--jt-cyan, #6fb5ab)',
  cyanDeep: 'var(--jt-cyan-deep, #5b948c)',
  cyanSoft: 'var(--jt-cyan-soft, rgba(111, 181, 171, .15))',
  // Alias kept for the screens that were originally written against the
  // old dark-theme accent's "neonLime" name — now maps to the configured
  // active/accent color instead of a hardcoded lime green.
  neonLime: 'var(--jt-cyan-deep, #5b948c)',
  // Semantic status colors — intentionally NOT tied to the Theme config,
  // since success/warning/danger meanings should stay recognizable
  // regardless of the admin's chosen brand palette.
  success: '#2c7a4b',
  warning: '#d68a10',
  danger: '#c0392b',
};

// Shared palette for the guest-facing BookingStatusScreen and
// ConfirmationScreen (both were using an identical local `COLORS` object
// before this was centralized).
export const GUEST_STATUS_COLORS = {
  deepBgStart: '#3d1220',
  deepBgEnd: '#3d1220',
  neonLime: '#5b948c',
  textSecondary: '#704352',
  cardBg: 'rgba(111, 181, 171, 0.08)',
  cardSolid: '#ffffff',
  pending: '#b3730a',
};

export const SORRY_OPEN_PLAY_UNAVAILABLE = "Thank you for your interest in joining our Open Play! As of the moment, our online payment system for Open Play is currently under maintenance. To secure your slot, please message us on Facebook or proceed via walk-in. See you on the court!";

// ---- Promos ----

/**
 * How a promo rule expresses its price (Admin -> Promo).
 *
 * FIXED replaces the hourly rate outright; PERCENT and AMOUNT are worked out
 * against whatever the normal rate band would have charged, so a club with
 * different prices per court keeps those differences under a "20% off".
 */
export const PROMO_RATE_MODES = {
  FIXED: 'fixed',
  PERCENT: 'percent',
  AMOUNT: 'amount',
};

/**
 * Labels for the mode picker, in the order they are offered.
 *
 * A function rather than a constant array because the third label carries the
 * club's currency symbol (Config -> System), which a module-level constant
 * cannot see. The symbol is passed in rather than read here so this file stays
 * import-free — it is the leaf every other module depends on.
 *
 * @param {string} [currencySymbol=DEFAULT_CURRENCY_SYMBOL]
 * @returns {Array<{value: string, label: string}>}
 */
export const getPromoRateModeOptions = (currencySymbol = DEFAULT_CURRENCY_SYMBOL) => [
  { value: PROMO_RATE_MODES.FIXED, label: 'Fixed price / hour' },
  { value: PROMO_RATE_MODES.PERCENT, label: '% off' },
  { value: PROMO_RATE_MODES.AMOUNT, label: `${currencySymbol} off` },
];

// ---- Open Play ----

/**
 * An openPlayJoins status meaning the admin took an already-confirmed player
 * back off a hosted session (AdminHostOpenPlayModal's player list).
 *
 * Deliberately its own status rather than reusing 'declined': declining
 * rejects a join REQUEST that was never approved, while this undoes an
 * approval the club already gave. Keeping them apart is what lets the audit
 * trail, the guest's tracking page and the export tell the two stories
 * apart.
 *
 * It sits outside ACTIVE_JOIN_STATUSES in openPlayUtil.js, so the spots
 * return to the session's cap the moment it is written — no separate
 * capacity bookkeeping.
 */
export const OPEN_PLAY_STATUS_REMOVED = 'removed';

/**
 * A hosted session the admin has called off.
 *
 * Sessions are cancelled, never deleted: the joins under one are receipts for
 * money already taken, and the record is what a refund or a dispute is argued
 * from. Every list that shows sessions already filters on `status === 'active'`
 * (see subscribeOpenPlaySessions callers and subscribeOpenPlayDay), so writing
 * this makes the session disappear from the admin rail, the guest rail and the
 * availability grid without any of them needing to learn a new status.
 */
export const OPEN_PLAY_SESSION_STATUS_CANCELLED = 'cancelled';

/**
 * Shown before cancelling a session that still has players on it. Deliberately
 * blunt about the knock-on effects, because they are not reversible from the
 * UI: the joins go with the session.
 */
export const OPEN_PLAY_CANCEL_CONFIRM_MESSAGE = 'Are you sure you want to cancel this Open Play?\n\nAll listed players or payments incurred will also be cancelled by the system.';

/**
 * The same prompt for a session nobody has joined yet — there are no players
 * or payments to warn about, and claiming there are would train admins to
 * click through the warning that matters.
 */
export const OPEN_PLAY_CANCEL_EMPTY_MESSAGE = 'Are you sure you want to cancel this Open Play?\n\nNobody has joined it yet, so its courts and hours simply go back to being bookable.';

// How many of the most recent openPlayJoins records a review screen streams.
// Generous enough to cover any realistic backlog while keeping these screens
// off an unbounded node — older joins stay in the database and remain
// reachable by export. Shared by the Admin -> Open Play tab and the
// Reservation Queue, which must agree on what "recent" means or the same
// join appears in one and not the other.
export const OPEN_PLAY_RECENT_JOINS_LIMIT = 500;

// How many characters of court names still sit comfortably after a time
// range on one line of an Open Play summary. Past this the courts move to
// their own line rather than wrapping mid-name. Shared by the join
// confirmation dialog and the join modal's summary box so both break at the
// same point.
export const OPEN_PLAY_COURTS_INLINE_MAX_CHARS = 22;

// How far ahead guests may book when the club hasn't set its own limit
// (Config -> System -> "Advance booking window"). Quoted in the admin
// field's placeholder/hint and used as the fallback in
// getBookingWindowDays (bookingUtils.js), so a config saved before the
// setting existed behaves like a club that chose 30.
export const DEFAULT_BOOKING_WINDOW_DAYS = 30;

// Bounds for that setting. The floor keeps at least today bookable — a 0
// would leave guests a booking screen with no selectable date at all — and
// the ceiling keeps the chip strip's page count sane.
export const MIN_BOOKING_WINDOW_DAYS = 1;
export const MAX_BOOKING_WINDOW_DAYS = 365;

// ---- Config -> Booking Settings ------------------------------------------
//
// Everything on that tab is stored under ONE config key, so the guest grid,
// the admin grid and the checkout guard all read it through one normaliser
// (getBookingSettings in bookingRulesUtil.js) instead of each re-deriving
// defaults from a handful of loose top-level fields.
//
//   bookingSettings: {
//     minBookingHours: 1,            // see getSelectionRuleIssue for how it counts
//     ruleDays: [], ruleCourts: [],  // where the rules apply; [] = everywhere
//     allowStaggered: true,          // false = hours on a date must connect
//     connectingSameCourtOnly: false,// only read when allowStaggered is false
//     blockBookings: [{ id, label, startHour, endHour, days, courts }],
//   }
export const BOOKING_SETTINGS_CONFIG_KEY = 'bookingSettings';

// A club that never opens the tab keeps today's behaviour: one hour minimum,
// any hours in any order, no blocks.
export const DEFAULT_MIN_BOOKING_HOURS = 1;
export const MIN_MIN_BOOKING_HOURS = 1;
// A day has 24 hours; past 12 a minimum stops being a rule and becomes a
// typo that silently makes the club unbookable.
export const MAX_MIN_BOOKING_HOURS = 12;

// Payment countdown used when Config has no paymentTimeoutMinutes. Mirrored
// as the fallback in database.rules.json's slotLocks expiry cap — keep the two
// in step.
export const DEFAULT_PAYMENT_TIMEOUT_MINUTES = 5;

export const DEFAULT_BOOKING_SETTINGS = {
  minBookingHours: DEFAULT_MIN_BOOKING_HOURS,
  ruleDays: [],
  ruleCourts: [],
  allowStaggered: true,
  connectingSameCourtOnly: false,
  blockBookings: [],
};

// A block's end hour may be 24 (midnight at the END of the day), which is
// what "7:00 PM – 12:00 MN" is stored as. Blocks never cross into the next
// date: that would make one button own cells on two dates with two separate
// bookings records behind them.
export const BLOCK_BOOKING_MAX_END_HOUR = 24;

// What a new block starts as in the editor — the evening run in the
// screenshot the feature was specced from. The admin edits it down.
export const DEFAULT_BLOCK_BOOKING_START_HOUR = 17;
export const DEFAULT_BLOCK_BOOKING_END_HOUR = 22;

// Guest-facing wording for the Reserve button's rule message. `{min}` and
// `{more}` are hour counts, `{date}` a short date ("Fri, Oct 16"), `{block}`
// a time range, `{court}` a court label.
export const BOOKING_RULE_MESSAGES = {
  minHours: 'Minimum booking is {min} hour{minPlural}. Add {more} more hour{morePlural} on {date} to continue.',
  minHoursTotal: 'Minimum booking is {min} hour{minPlural}. Add {more} more hour{morePlural} to continue.',
  staggered: 'Your hours on {date} must be connecting (e.g. 6–7 PM and 7–8 PM). Remove the gap to continue.',
  minHoursPerCourt: 'Minimum booking is {min} hour{minPlural} per court. Add {more} more hour{morePlural} on {court} ({date}) to continue.',
  courtStaggered: 'Your hours on {court} ({date}) must be connecting (e.g. 6–7 PM and 7–8 PM). Remove the gap to continue.',
  partialBlock: 'The {block} block on {court} ({date}) is booked as a whole. Select the whole block or remove it.',
};

// The hour the GUEST booking grid starts its day on (0-23).
//
// A club open round the clock still has nobody booking at 2AM, and starting
// the grid at midnight made a guest scroll past six dead rows before
// reaching a plausible hour. The slots before this one aren't hidden — they
// wrap round to the bottom of the grid, so an overnight booking is still
// reachable (see orderSlotsFromDayStart in bookingUtils.js).
//
// Display order only: sortTimeSlots stays chronological everywhere else, and
// the admin availability grid is deliberately unchanged — an admin managing
// the day wants it in clock order.
export const GUEST_DAY_START_HOUR = 6;


// ============================================================
// PARTIAL PAYMENT (DOWNPAYMENT)
// ============================================================
// A club may let a guest secure a reservation by paying only part of the
// total up front (Config -> Pricing -> "Partial Payment Allowed (%)"). The
// admin confirms such a booking with "Confirm (Partial)" instead of
// "Confirm", records what was actually received, and the reservation carries
// a balance until someone presses "Mark Fully Paid".
//
// The configured percentage is a MINIMUM, not a fixed amount: 50 means the
// admin may accept anything from half the total up to the full amount.

/** Config default — 0 (and any blank/invalid value) means full payment is required. */
export const DEFAULT_PARTIAL_PAYMENT_PERCENT = 0;

/** Upper bound for the config field; 100% is simply a full payment. */
export const MAX_PARTIAL_PAYMENT_PERCENT = 100;

/**
 * `paymentStatus` on a booking record.
 *
 * ABSENT on every booking made before this feature existed, and on every one
 * confirmed the ordinary way since — which is why nothing reads this field
 * with `=== PAYMENT_STATUS_PAID`. Only PARTIAL is ever tested for, so a
 * record without the field behaves exactly as it always has.
 */
export const PAYMENT_STATUS_PARTIAL = 'partial';
export const PAYMENT_STATUS_PAID = 'paid';

/** The short badge printed on a partially-paid reservation, everywhere it appears. */
export const PARTIAL_PAYMENT_BADGE = 'PARTIAL';

/**
 * Sub-label beside the optional Email Address field on the guest booking-details
 * form (UserInfoForm.jsx). The confirmation-email hint shows only while
 * Config -> System -> Send booking emails is on; otherwise the field is just
 * marked optional, since no email would be sent.
 */
export const GUEST_EMAIL_FIELD_HINT = 'Fill up to receive email confirmation of your booking';
export const GUEST_EMAIL_FIELD_OPTIONAL_HINT = 'optional';

/** Guest booking-details checkbox label, before it is ticked. */
export const PARTIAL_PAYMENT_GUEST_LABEL = 'Partial payment';

/**
 * Guest booking-details checkbox label once ticked; `{percent}` is replaced
 * with the configured "Partial Payment Allowed (%)".
 */
export const PARTIAL_PAYMENT_GUEST_REQUIRED_LABEL = 'Required partial payment is {percent}%';

/**
 * Payment summary labels on the guest confirmation screen and the booking
 * reference PDF (ConfirmationPaymentSummary.jsx, utils/pdfReceipt.js).
 * For a partial payment both show the full total, what was paid and the
 * remaining balance; a full payment shows only the paid line.
 */
export const PAYMENT_SUMMARY_TOTAL_LABEL = 'Total';
export const PAYMENT_SUMMARY_PAID_LABEL = 'Total Paid';
export const PAYMENT_SUMMARY_BALANCE_LABEL = 'Remaining balance';

// ---- Track Booking screen (components/BookingStatusScreen.jsx) ----

export const BOOKING_STATUS_TITLE = 'Track your Booking Status';
export const BOOKING_STATUS_SUBTITLE = 'Please paste the booking reference number on the search box and your court reservation status will appear.';

/**
 * Shown under "Booking Confirmed!". `{courtName}` is replaced with
 * config.courtName.
 */
export const BOOKING_STATUS_CONFIRMED_MESSAGE = 'Your booking has been approved. Thank you for choosing {courtName} as your Pickleball Venue. We look forward to seeing you!';

/** Fallback when the club hasn't set config.courtName (same as ConfirmationScreen). */
export const DEFAULT_COURT_NAME = 'Your Court Name';

/**
 * Tag beside "Total Paid" on a partial payment. `{percent}` is the guest's
 * agreed percentage; PARTIAL_PAYMENT_TAG is used when there isn't one (an
 * admin-recorded downpayment can be any amount above the minimum).
 */
export const PARTIAL_PAYMENT_PERCENT_TAG = '{percent}% partial';
export const PARTIAL_PAYMENT_TAG = 'partial';

/**
 * The "Manage availability" grid's own status for a cell whose reservation
 * is confirmed but not paid off (AdminTimeSlotSelection.jsx -> STATUS_OPTIONS).
 *
 * DISPLAY ONLY: it is reached by taking a payment, never by picking it from
 * the cell's dropdown, where it renders disabled. An admin who needs to
 * change what is owed does it in the booking's own modal.
 */
export const GRID_STATUS_PARTIAL = 'partial';

/**
 * Every grid status that means a REAL booking record sits on the cell, as
 * opposed to a slotOverrides string.
 *
 * Shared by the grid (which decides what a change to the cell means) and
 * AdminBooking's save (which decides whether to update that booking or to
 * create one) — they have to agree, and they used to say it inline in four
 * places, which is four places to forget 'partial' in.
 */
export const GRID_BOOKED_CELL_STATUSES = ['booked', 'processing', GRID_STATUS_PARTIAL];

/**
 * The status options of the "Manage availability" grid dropdown, in display
 * order. Config -> System -> "Availability status options" ticks which of
 * them the dropdown shows (see getEnabledGridStatuses); `defaultEnabled` is
 * what a club gets before it has saved that setting.
 *
 * 'partial' is `selectable: false`: it can be DISPLAYED as a cell's current
 * status but never picked — a cell becomes partial by taking a downpayment
 * (Confirm (Partial)), not from this dropdown.
 */
export const ADMIN_GRID_STATUS_OPTIONS = [
  { value: 'booked', label: 'Booked', defaultEnabled: true },
  { value: GRID_STATUS_PARTIAL, label: 'Partial', selectable: false, defaultEnabled: true },
  { value: 'open', label: 'Open', defaultEnabled: true },
  { value: 'processing', label: 'Processing', defaultEnabled: true },
  { value: 'passed', label: 'Passed', defaultEnabled: true },
  { value: SLOT_STATUS_CLOSED, label: 'Closed', defaultEnabled: false },
];

// ---- Availability images (Admin -> Booking -> Grid View -> Generate images) ----

/** Landscape availability image size, in px — the 3:2 shape of the approved grid mockups. */
export const AVAILABILITY_IMAGE_LANDSCAPE = { width: 1536, height: 1024 };

/** Mobile "story" size, in px — fills a phone screen (FB/IG stories, Messenger, Viber, group chats). */
export const AVAILABILITY_IMAGE_STORY = { width: 1080, height: 1920 };

/** Mobile feed size, in px — the tallest shape Facebook/Instagram feed posts allow (4:5). */
export const AVAILABILITY_IMAGE_PORTRAIT = { width: 1080, height: 1350 };

/**
 * How the availability image dialog groups its previews. Each template in
 * AVAILABILITY_IMAGE_TEMPLATES names one of these ids; `short` is what the
 * audit trail appends to the template label ("Full grid (dark) - Story").
 */
export const AVAILABILITY_IMAGE_GROUPS = [
  // Phone sizes first: that is where most guests will see these images.
  { id: 'story', label: 'Mobile story (1080\u00d71920)', short: 'Story', mobile: true },
  { id: 'portrait', label: 'Mobile feed post (1080\u00d71350)', short: 'Feed', mobile: true },
  { id: 'wide', label: 'Wide \u00b7 desktop & Facebook (1536\u00d71024)', short: 'Wide', mobile: false },
];

/**
 * Availability-image settings (the gear in the Availability images dialog).
 * They only affect the generated images, never the live booking grid.
 *
 * Font sizes are exact pixel sizes in the exported image (a story is
 * 1080px wide). Left blank (null) the size is automatic: worked out from the
 * row height and column width so the text fits.
 */
export const AVAILABILITY_IMAGE_TEXT_PX_MIN = 8;
export const AVAILABILITY_IMAGE_TEXT_PX_MAX = 120;
/** BOOKED cell colours by default — the guest grid's grey. */
export const AVAILABILITY_IMAGE_BOOKED_BG_DEFAULT = '#dfe1e6';
export const AVAILABILITY_IMAGE_BOOKED_TEXT_DEFAULT = '#7c7c7c';
export const AVAILABILITY_IMAGE_DEFAULT_SETTINGS = {
  timePx: null,
  cellPx: null,
  bookedBg: AVAILABILITY_IMAGE_BOOKED_BG_DEFAULT,
  bookedText: AVAILABILITY_IMAGE_BOOKED_TEXT_DEFAULT,
};
/** localStorage key the settings are remembered under, per browser. */
export const AVAILABILITY_IMAGE_SETTINGS_STORAGE_KEY = 'availabilityImageSettings';

/** Line under the club name in the availability image headers. */
export const AVAILABILITY_IMAGE_TAGLINE = 'Powered by The Website Pal';

/**
 * Theme colours the availability images fall back to when the club's
 * Config -> Theme value is missing or malformed — the site theme's own
 * defaults (ConfigContext DEFAULT_CONFIG).
 */
export const AVAILABILITY_IMAGE_THEME_FALLBACK = {
  activeColor: '#6fb5ab',
  textColor: '#3d1220',
};

/**
 * What one court-hour can say on an availability image. The admin grid's
 * statuses collapse into these (see toImageCellStatus in
 * availabilitySnapshotUtil.js) — a guest only needs to know whether the hour
 * can be booked, not that it is "processing" or "partial".
 */
export const AVAILABILITY_IMAGE_CELL = {
  OPEN: 'open',
  BOOKED: 'booked',
  OPEN_PLAY: 'openPlay',
  CLOSED: 'closed',
  PASSED: 'passed',
};

/**
 * The word in a timeslot cell of the full-grid images, per
 * AVAILABILITY_IMAGE_CELL value — the same words the guest booking grid uses
 * (shown upper-case). Open Play has its own two-line cell (GuestStyleCell).
 */
export const AVAILABILITY_IMAGE_CELL_LABELS = {
  open: 'Open',
  booked: 'Booked',
  openPlay: 'Open Play',
  closed: 'Closed',
  passed: 'Passed',
};

/**
 * The image templates offered, in the order the dialog shows them, with the
 * size each one exports at and the preview group it sits in. The id is also
 * the template's key in components/availabilityImage/templateRegistry.js,
 * part of the downloaded file name, and what the audit trail records.
 * `format` is passed to the component, so one mobile component draws both
 * the story and the feed-post size. `rowMode` is passed too: 'hourly' always
 * draws one row per timeslot; 'ranges' merges hours into time ranges when
 * one row per hour would be too small to read (see planGridRows).
 */
export const AVAILABILITY_IMAGE_TEMPLATES = [
  { id: 'fullGridDarkStory', label: 'Full grid (dark) \u00b7 Time ranges', group: 'story', format: 'story', rowMode: 'ranges', ...AVAILABILITY_IMAGE_STORY },
  { id: 'fullGridLightStory', label: 'Full grid (light) \u00b7 Time ranges', group: 'story', format: 'story', rowMode: 'ranges', ...AVAILABILITY_IMAGE_STORY },
  { id: 'fullGridDarkStoryHourly', label: 'Full grid (dark) \u00b7 Per hour', group: 'story', format: 'story', rowMode: 'hourly', ...AVAILABILITY_IMAGE_STORY },
  { id: 'fullGridLightStoryHourly', label: 'Full grid (light) \u00b7 Per hour', group: 'story', format: 'story', rowMode: 'hourly', ...AVAILABILITY_IMAGE_STORY },
  { id: 'fullGridDarkPortrait', label: 'Full grid (dark) \u00b7 Time ranges', group: 'portrait', format: 'portrait', rowMode: 'ranges', ...AVAILABILITY_IMAGE_PORTRAIT },
  { id: 'fullGridLightPortrait', label: 'Full grid (light) \u00b7 Time ranges', group: 'portrait', format: 'portrait', rowMode: 'ranges', ...AVAILABILITY_IMAGE_PORTRAIT },
  { id: 'fullGridDarkPortraitHourly', label: 'Full grid (dark) \u00b7 Per hour', group: 'portrait', format: 'portrait', rowMode: 'hourly', ...AVAILABILITY_IMAGE_PORTRAIT },
  { id: 'fullGridLightPortraitHourly', label: 'Full grid (light) \u00b7 Per hour', group: 'portrait', format: 'portrait', rowMode: 'hourly', ...AVAILABILITY_IMAGE_PORTRAIT },
  { id: 'fullGridDark', label: 'Full grid (dark) \u00b7 Time ranges', group: 'wide', rowMode: 'ranges', ...AVAILABILITY_IMAGE_LANDSCAPE },
  { id: 'fullGridLight', label: 'Full grid (light) \u00b7 Time ranges', group: 'wide', rowMode: 'ranges', ...AVAILABILITY_IMAGE_LANDSCAPE },
  { id: 'fullGridDarkHourly', label: 'Full grid (dark) \u00b7 Per hour', group: 'wide', rowMode: 'hourly', ...AVAILABILITY_IMAGE_LANDSCAPE },
  { id: 'fullGridLightHourly', label: 'Full grid (light) \u00b7 Per hour', group: 'wide', rowMode: 'hourly', ...AVAILABILITY_IMAGE_LANDSCAPE },
];

// ---- Multi-day availability images (the date range in the dialog) ----

/** Longest date range one multi-day image covers — a week, as in a weekly timetable. */
export const AVAILABILITY_IMAGE_MAX_RANGE_DAYS = 7;

/** Multi-day image size, in px. Wider than a single day's: every date gets a column per court. */
export const AVAILABILITY_IMAGE_MULTI_DAY = { width: 1920, height: 1280 };

/** Words on the multi-day images and the range picker. */
export const AVAILABILITY_IMAGE_MULTI_DAY_LABELS = {
  TITLE: 'Court availability',
  WEEK_TITLE: 'Weekly court availability',
  // Under an after-midnight row, where each column is a different date.
  NEXT_DAY: 'Next day',
  RANGE_FROM: 'From',
  RANGE_TO: 'To',
  RANGE_HINT: `Pick one date, or a range of up to ${AVAILABILITY_IMAGE_MAX_RANGE_DAYS} days for one combined image.`,
  LOADING: 'Loading availability…',
};

/** Preview group for the multi-day images (see AVAILABILITY_IMAGE_GROUPS). */
export const AVAILABILITY_IMAGE_MULTI_DAY_GROUPS = [
  { id: 'multiDay', label: 'Multi-day \u00b7 desktop & Facebook (1920\u00d71280)', short: 'Multi-day', mobile: false },
];

/**
 * The templates offered when the dialog's range covers more than one date —
 * the single-day list above is used for one date. Same fields as
 * AVAILABILITY_IMAGE_TEMPLATES; the ids are keys in templateRegistry.js.
 * The phone sizes (story / feed post) draw the compact days-across table
 * (MultiDayCompactTable); the wide size gives every court its own column.
 */
export const AVAILABILITY_IMAGE_MULTI_DAY_TEMPLATES = [
  { id: 'multiDayDarkStory', label: 'Multi-day grid (dark) \u00b7 Time ranges', group: 'story', format: 'story', rowMode: 'ranges', ...AVAILABILITY_IMAGE_STORY },
  { id: 'multiDayLightStory', label: 'Multi-day grid (light) \u00b7 Time ranges', group: 'story', format: 'story', rowMode: 'ranges', ...AVAILABILITY_IMAGE_STORY },
  { id: 'multiDayDarkStoryHourly', label: 'Multi-day grid (dark) \u00b7 Per hour', group: 'story', format: 'story', rowMode: 'hourly', ...AVAILABILITY_IMAGE_STORY },
  { id: 'multiDayLightStoryHourly', label: 'Multi-day grid (light) \u00b7 Per hour', group: 'story', format: 'story', rowMode: 'hourly', ...AVAILABILITY_IMAGE_STORY },
  { id: 'multiDayDarkPortrait', label: 'Multi-day grid (dark) \u00b7 Time ranges', group: 'portrait', format: 'portrait', rowMode: 'ranges', ...AVAILABILITY_IMAGE_PORTRAIT },
  { id: 'multiDayLightPortrait', label: 'Multi-day grid (light) \u00b7 Time ranges', group: 'portrait', format: 'portrait', rowMode: 'ranges', ...AVAILABILITY_IMAGE_PORTRAIT },
  { id: 'multiDayDarkPortraitHourly', label: 'Multi-day grid (dark) \u00b7 Per hour', group: 'portrait', format: 'portrait', rowMode: 'hourly', ...AVAILABILITY_IMAGE_PORTRAIT },
  { id: 'multiDayLightPortraitHourly', label: 'Multi-day grid (light) \u00b7 Per hour', group: 'portrait', format: 'portrait', rowMode: 'hourly', ...AVAILABILITY_IMAGE_PORTRAIT },
  { id: 'multiDayDark', label: 'Multi-day grid (dark) \u00b7 Time ranges', group: 'multiDay', rowMode: 'ranges', ...AVAILABILITY_IMAGE_MULTI_DAY },
  { id: 'multiDayLight', label: 'Multi-day grid (light) \u00b7 Time ranges', group: 'multiDay', rowMode: 'ranges', ...AVAILABILITY_IMAGE_MULTI_DAY },
  { id: 'multiDayDarkHourly', label: 'Multi-day grid (dark) \u00b7 Per hour', group: 'multiDay', rowMode: 'hourly', ...AVAILABILITY_IMAGE_MULTI_DAY },
  { id: 'multiDayLightHourly', label: 'Multi-day grid (light) \u00b7 Per hour', group: 'multiDay', rowMode: 'hourly', ...AVAILABILITY_IMAGE_MULTI_DAY },
];

/**
 * How long the dialog waits after the last change to the dates before it
 * loads them, so stepping through a date picker doesn't start a read (and a
 * round of images) for every date passed on the way.
 */
export const AVAILABILITY_IMAGE_RANGE_DEBOUNCE_MS = 400;

/**
 * Legend words on the phone-sized multi-day images (MultiDayCompactTable).
 * The slots carry their own status words, so the legend only explains the
 * promo outline and, for stacked slots, which number is which court.
 */
export const AVAILABILITY_IMAGE_COMPACT_LEGEND = {
  promo: 'Promo rate',
  // Before the court key, when a club's court names aren't plain "Court N".
  courts: 'Courts',
};


// ============================================================
// AUDIT TRAIL
// ============================================================
// The audit log's enums and label maps live here, with every other constant
// in the app, rather than beside the functions that write the log. That
// keeps this file import-free (it is a leaf module imported almost
// everywhere) while still letting GRID_STATUS_ACTIONS below reference the
// action types directly. src/utils/auditLogUtil.js re-exports all of these,
// so importing them from either place works.

export const AUDIT_ACTION_TYPES = {
  BOOKING_SUBMITTED: 'booking_submitted',
  BOOKING_CONFIRMED: 'booking_confirmed',
  BOOKING_DECLINED: 'booking_declined',
  BOOKING_CANCELLED: 'booking_cancelled',
  // Catch-all for a status change that isn't a plain confirm/decline/cancel —
  // e.g. the "Manage availability" grid moving a cell between booked/
  // processing/open directly, or a booking reopened from disapproved back to
  // pending. Still carries statusOld/statusNew like the three above.
  BOOKING_STATUS_CHANGED: 'booking_status_changed',
  // One hour taken off a booking that still has other hours left — the
  // "Manage availability" grid releasing a single cell. Distinct from
  // BOOKING_CANCELLED, which means the whole reservation is gone: the
  // question this answers later is "why is this booking an hour shorter than
  // the guest remembers?". Not a status change — the booking's status is
  // untouched — so it stays out of STATUS_CHANGE_ACTIONS below.
  BOOKING_SLOT_REMOVED: 'booking_slot_removed',
  // Confirmed against a downpayment rather than the full amount ("Confirm
  // (Partial)"). Kept apart from BOOKING_CONFIRMED because the question it
  // answers later is "who let this one on court owing money, and how much?"
  // — the amount received and the balance left ride along in `changes`.
  BOOKING_PARTIALLY_PAID: 'booking_partially_paid',
  // The balance on a partially-paid reservation settled ("Mark Fully Paid").
  // Not a status change — the booking was already confirmed — so it stays out
  // of STATUS_CHANGE_ACTIONS below.
  BOOKING_FULLY_PAID: 'booking_fully_paid',
  // One run of Booking Management -> Cleanup Booking. Logged once per run
  // rather than once per record: 4,000 identical entries would bury the trail
  // it is written into, and the question worth answering later is "who
  // cleared what, and when?", which one entry carrying the range and the
  // count answers completely. Its entityId is the run's own timestamp-based
  // id — there is no surviving record to point at, which is the point.
  BOOKINGS_CLEANED_UP: 'bookings_cleaned_up',
  OPENPLAY_SESSION_CREATED: 'openplay_session_created',
  OPENPLAY_SESSION_EDITED: 'openplay_session_edited',
  // The whole session called off, taking its players with it. Separate from
  // OPENPLAY_PLAYER_REMOVED (which each affected join still logs) because the
  // question being answered later is "why did this session vanish?", not "why
  // did this one person come off it?".
  OPENPLAY_SESSION_CANCELLED: 'openplay_session_cancelled',
  OPENPLAY_PLAYER_JOINED: 'openplay_player_joined',
  OPENPLAY_PLAYER_CONFIRMED: 'openplay_player_confirmed',
  OPENPLAY_PLAYER_DECLINED: 'openplay_player_declined',
  OPENPLAY_PLAYER_REMOVED: 'openplay_player_removed',
  // Promos rewrite what guests are charged, so every change to one is
  // recorded — the trail is where "why was this hour ₱100 last Tuesday?"
  // gets answered once the promo itself has been edited or deleted.
  PROMO_CREATED: 'promo_created',
  PROMO_UPDATED: 'promo_updated',
  PROMO_DELETED: 'promo_deleted',
  PROMO_ENABLED: 'promo_enabled',
  PROMO_DISABLED: 'promo_disabled',
  // An hour on a court taken out of service, or put back, from the "Manage
  // availability" grid (STATUS_OPTIONS -> Closed). Logged separately from the
  // BOOKING_* actions because the thing acted on is the SLOT, not a booking:
  // an empty hour can be closed, which used to leave no trace at all. When a
  // booking was sitting on the closed hour, its own cancellation still logs
  // its own BOOKING_CANCELLED entry alongside this one.
  SLOT_CLOSED: 'slot_closed',
  SLOT_REOPENED: 'slot_reopened',
  // Any other manual status set on an EMPTY hour from the grid (e.g. open ->
  // processing, passed -> open). Hours carrying a booking log the booking's
  // own entry instead, and closing/reopening log the two actions above.
  SLOT_STATUS_CHANGED: 'slot_status_changed',
  // An availability image (Grid View -> Generate images) saved for posting.
  // Not a change to any data — logged so the club can see who published
  // which day's availability, and when.
  AVAILABILITY_IMAGE_DOWNLOADED: 'availability_image_downloaded',
  // A guest saved a payment QR from the checkout screen (the "Download QR"
  // button or the full-screen viewer). Not a change to any data — logged so
  // the club can see which account a guest took away to pay into.
  PAYMENT_QR_DOWNLOADED: 'payment_qr_downloaded',
  // A redeploy of this club's site, written by the Redeploy client workflow
  // rather than by the app — the three entries bracket one GitHub Actions
  // run, so an admin can see their click land, and see how it ended, without
  // a GitHub account. entityId is the run id, which is also what the run URL
  // in `summary` points at.
  REDEPLOY_STARTED: 'redeploy_started',
  REDEPLOY_SUCCESS: 'redeploy_success',
  REDEPLOY_FAILED: 'redeploy_failed',
  // The first baseline written for a club that has never released through
  // the button. NOT a deploy: it only records the moment from which a
  // config change counts as "saved but not live yet" (redeployUtil's
  // ensureDeployStatusBaseline).
  DEPLOY_BASELINE_SET: 'deploy_baseline_set',
  // Config -> Theme -> Time Slot Button Colors saved with a change (including
  // a reset back to the defaults). Logged on Save, not on each pick, since
  // nothing reaches guests until the config is saved.
  SLOT_COLORS_UPDATED: 'slot_colors_updated',
  // Config -> Theme -> UI Design switched between versions. Logged on Save,
  // like the slot colours, since guests see nothing until the config is saved
  // and redeployed.
  UI_DESIGN_UPDATED: 'ui_design_updated',
  // Config -> Booking Settings saved with a change (minimum hours, the
  // staggered/connecting rules, or the block bookings list). Logged on Save
  // like the two above.
  BOOKING_SETTINGS_UPDATED: 'booking_settings_updated',
  // Config -> System -> "Ask reason for closing timeslot" switched on or off.
  // Logged on Save like the three above.
  CLOSE_REASON_PROMPT_UPDATED: 'close_reason_prompt_updated',
  // Config -> Theme -> Open in Browser Popup switched between versions.
  // Logged on Save like the UI design, since guests see nothing until the
  // config is saved and redeployed.
  BROWSER_PROMPT_UPDATED: 'browser_prompt_updated',
  // Config -> Theme -> Rate Separators: shown/hidden or the design changed.
  // Logged on Save like the UI design.
  RATE_SEPARATORS_UPDATED: 'rate_separators_updated',
};

export const ENTITY_TYPES = {
  BOOKING: 'booking',
  OPENPLAY_SESSION: 'openPlaySession',
  OPENPLAY_JOIN: 'openPlayJoin',
  // A (date, court, hour) cell rather than a stored record — its entityId is
  // the slotOverrides path it was written to, "yyyy-MM-dd/court-N", since a
  // slot has no Firebase key of its own.
  SLOT: 'slot',
  // config.promos entries. entityId is the promo's own id, which survives
  // edits and outlives deletion, so a deleted promo's history stays joined up.
  PROMO: 'promo',
  // One run of the Redeploy client workflow. entityId is the GitHub run id —
  // not a record in this database at all, which is the point: the trail has
  // to survive the thing it describes living somewhere else.
  DEPLOYMENT: 'deployment',
  // A setting in /config. entityId names the setting group (e.g.
  // SLOT_BUTTON_COLORS_CONFIG_KEY) — the config node has no per-entry ids.
  CONFIG: 'config',
};

// Display labels for the Audit Trail screen — kept here rather than
// recomputed in the component so a new action type only needs one spot.
export const AUDIT_ACTION_LABELS = {
  [AUDIT_ACTION_TYPES.BOOKING_SUBMITTED]: 'Booking submitted',
  [AUDIT_ACTION_TYPES.BOOKING_CONFIRMED]: 'Booking confirmed',
  [AUDIT_ACTION_TYPES.BOOKING_DECLINED]: 'Booking declined',
  [AUDIT_ACTION_TYPES.BOOKING_CANCELLED]: 'Booking cancelled',
  [AUDIT_ACTION_TYPES.BOOKING_STATUS_CHANGED]: 'Booking status changed',
  [AUDIT_ACTION_TYPES.BOOKING_SLOT_REMOVED]: 'Booking hour removed',
  [AUDIT_ACTION_TYPES.BOOKING_PARTIALLY_PAID]: 'Booking confirmed (partial payment)',
  [AUDIT_ACTION_TYPES.BOOKING_FULLY_PAID]: 'Balance settled',
  [AUDIT_ACTION_TYPES.BOOKINGS_CLEANED_UP]: 'Dirty bookings deleted',
  [AUDIT_ACTION_TYPES.OPENPLAY_SESSION_CREATED]: 'Open Play session created',
  [AUDIT_ACTION_TYPES.OPENPLAY_SESSION_EDITED]: 'Open Play session edited',
  [AUDIT_ACTION_TYPES.OPENPLAY_SESSION_CANCELLED]: 'Open Play session cancelled',
  [AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_JOINED]: 'Open Play player joined',
  [AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_CONFIRMED]: 'Open Play player confirmed',
  [AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_DECLINED]: 'Open Play player declined',
  [AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_REMOVED]: 'Open Play player removed',
  [AUDIT_ACTION_TYPES.PROMO_CREATED]: 'Promo created',
  [AUDIT_ACTION_TYPES.PROMO_UPDATED]: 'Promo updated',
  [AUDIT_ACTION_TYPES.PROMO_DELETED]: 'Promo deleted',
  [AUDIT_ACTION_TYPES.PROMO_ENABLED]: 'Promo turned on',
  [AUDIT_ACTION_TYPES.PROMO_DISABLED]: 'Promo turned off',
  [AUDIT_ACTION_TYPES.SLOT_CLOSED]: 'Time slot closed',
  [AUDIT_ACTION_TYPES.SLOT_REOPENED]: 'Time slot reopened',
  [AUDIT_ACTION_TYPES.SLOT_STATUS_CHANGED]: 'Time slot status changed',
  [AUDIT_ACTION_TYPES.AVAILABILITY_IMAGE_DOWNLOADED]: 'Availability image downloaded',
  [AUDIT_ACTION_TYPES.PAYMENT_QR_DOWNLOADED]: 'Payment QR downloaded',
  [AUDIT_ACTION_TYPES.REDEPLOY_STARTED]: 'Redeployment started',
  [AUDIT_ACTION_TYPES.REDEPLOY_SUCCESS]: 'Redeployment success',
  [AUDIT_ACTION_TYPES.REDEPLOY_FAILED]: 'Redeployment failed',
  [AUDIT_ACTION_TYPES.DEPLOY_BASELINE_SET]: 'Deploy baseline recorded',
  [AUDIT_ACTION_TYPES.SLOT_COLORS_UPDATED]: 'Time slot button colors updated',
  [AUDIT_ACTION_TYPES.UI_DESIGN_UPDATED]: 'UI design changed',
  [AUDIT_ACTION_TYPES.RATE_SEPARATORS_UPDATED]: 'Rate separators changed',
  [AUDIT_ACTION_TYPES.BOOKING_SETTINGS_UPDATED]: 'Booking settings updated',
  [AUDIT_ACTION_TYPES.CLOSE_REASON_PROMPT_UPDATED]: 'Close reason prompt changed',
  [AUDIT_ACTION_TYPES.BROWSER_PROMPT_UPDATED]: 'Open in browser popup changed',
};

// Which action types are genuinely a status transition — used by the admin
// screen to decide whether to render the Status Old -> New column for a row.
export const STATUS_CHANGE_ACTIONS = new Set([
  AUDIT_ACTION_TYPES.BOOKING_CONFIRMED,
  // Confirms the booking as well as recording the downpayment, so it carries
  // the same pending -> confirmed transition the plain confirm does.
  AUDIT_ACTION_TYPES.BOOKING_PARTIALLY_PAID,
  AUDIT_ACTION_TYPES.BOOKING_DECLINED,
  AUDIT_ACTION_TYPES.BOOKING_CANCELLED,
  AUDIT_ACTION_TYPES.BOOKING_STATUS_CHANGED,
  AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_CONFIRMED,
  AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_DECLINED,
  // Carries confirmed -> removed, so the Old -> New column reads correctly
  // for a player taken off a session as well as for one declined.
  AUDIT_ACTION_TYPES.OPENPLAY_PLAYER_REMOVED,
  // Carries active -> cancelled for the session itself.
  AUDIT_ACTION_TYPES.OPENPLAY_SESSION_CANCELLED,
  // On/off carries enabled -> disabled in the same Old -> New column.
  AUDIT_ACTION_TYPES.PROMO_ENABLED,
  AUDIT_ACTION_TYPES.PROMO_DISABLED,
  // These carry the cell's GRID status (open/booked/processing/passed ->
  // closed, and back), not a booking status — the same Old -> New column
  // reads correctly for both.
  AUDIT_ACTION_TYPES.SLOT_CLOSED,
  AUDIT_ACTION_TYPES.SLOT_REOPENED,
  AUDIT_ACTION_TYPES.SLOT_STATUS_CHANGED,
]);

// Last-resort actor label, used only when no signed-in account can be read
// (see resolveAdminActor). Kept exported so call sites and the admin screen
// agree on the string instead of spelling it inline.
export const PLACEHOLDER_ADMIN_ACTOR = 'admin';
export const GUEST_ACTOR = 'guest';

/**
 * Maps the status a "Manage availability" grid edit lands on to the audit
 * action that MEANS that, so a booking confirmed from the grid logs the same
 * `booking_confirmed` action as one confirmed from the Reservation Queue or
 * the Manage Booking modal. A status not listed here (e.g. back to 'pending')
 * has no more specific meaning and falls back to BOOKING_STATUS_CHANGED.
 */
export const GRID_STATUS_ACTIONS = {
  confirmed: AUDIT_ACTION_TYPES.BOOKING_CONFIRMED,
  cancelled: AUDIT_ACTION_TYPES.BOOKING_CANCELLED,
  disapproved: AUDIT_ACTION_TYPES.BOOKING_DECLINED,
};

/** How many days back the Audit Trail screen loads on first open. */
export const DEFAULT_AUDIT_DAYS = 3;

// ---- Booking cleanup (Booking Management -> Cleanup Booking) ----
//
// Every booking made through a screen writes a `booking_submitted` audit
// entry; a record written straight to the database over the REST API does
// not. These constants name the groups that test sorts records into, and the
// wording the screen explains them with — kept here so the labels an admin
// reads before deleting 4,000 records live beside the values they describe,
// rather than inline in the component.

/** The three buckets a booking record falls into. */
export const CLEANUP_GROUPS = {
  /** No submit record, and not an in-flight checkout: what this screen exists to remove. */
  DIRTY: 'dirty',
  /** No submit record because the guest never finished paying — a real, harmless record. */
  ABANDONED: 'abandoned',
  /** Has its submit record. Never deletable from this screen. */
  CLEAN: 'clean',
};

/** Short heading per group. */
export const CLEANUP_GROUP_LABELS = {
  [CLEANUP_GROUPS.DIRTY]: 'Dirty bookings — no "Booking submitted" record',
  [CLEANUP_GROUPS.ABANDONED]: 'Abandoned checkouts — awaiting payment',
  [CLEANUP_GROUPS.CLEAN]: 'Genuine bookings — submitted through the app',
};

/**
 * The sentence under each heading. An admin about to delete in bulk needs to
 * know what the list in front of them actually proves, so these say what the
 * test found rather than just naming it.
 */
export const CLEANUP_GROUP_DESCRIPTIONS = {
  [CLEANUP_GROUPS.DIRTY]: 'These booking records exist in the database, but nothing in the audit trail says anyone ever submitted them through the booking screen. Every booking made through the app — by a guest or by an admin — writes a "Booking submitted" entry at the moment it becomes reviewable. A record written directly to the database over the API never does. These are safe to delete unless your club has bookings older than the audit trail itself (see the note above the list).',
  [CLEANUP_GROUPS.ABANDONED]: 'These have no "Booking submitted" entry either, but for an innocent reason: a booking only gets one when the guest uploads a payment receipt, and these are still waiting on that. Most are abandoned checkouts; a few may be guests mid-payment right now. They are listed separately and left unticked for that reason.',
  [CLEANUP_GROUPS.CLEAN]: 'These carry a "Booking submitted" audit entry, so they came through the app. They are shown only as a count, and cannot be selected or deleted here.',
};

/** Per-row badge text. */
export const CLEANUP_GROUP_BADGES = {
  [CLEANUP_GROUPS.DIRTY]: 'No submit record',
  [CLEANUP_GROUPS.ABANDONED]: 'Awaiting payment',
  [CLEANUP_GROUPS.CLEAN]: 'Submitted',
};

/**
 * How far either side of the selected range the audit log is searched for
 * "Booking submitted" entries.
 *
 * The entry is written when the guest uploads a receipt, minutes after the
 * booking record itself — and "minutes after" can fall the other side of a
 * range boundary. Searching only the exact range would make a genuine booking
 * created at 4:59pm, submitted at 5:01pm, read as dirty. The margin is
 * generous because the cost of it is a few extra small records, while the
 * cost of missing an entry is a real booking in the delete list.
 */
export const CLEANUP_AUDIT_MARGIN_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Records per multi-path delete request. One update carrying every path for
 * 4,000 bookings is a single request Firebase may refuse outright, and a
 * refusal there means nothing was deleted at all.
 */
export const CLEANUP_DELETE_CHUNK_SIZE = 200;

/** How many days back the cleanup screen's range defaults to. */
export const CLEANUP_DEFAULT_RANGE_DAYS = 7;

/** Rows shown per page in the cleanup list. */
export const CLEANUP_PAGE_SIZE = 25;

// ---- Loading timeouts ----

/**
 * The longest any loading spinner may run before the app gives up and shows
 * ConnectionErrorScreen instead (see hooks/useLoadingTimeout.js). Try Again
 * on that screen starts the same countdown over.
 *
 * Every loading state in this app waits on a Firebase read or subscription,
 * and neither reports failure on a dead or captive-portal connection — the
 * callback simply never fires. Without this limit the spinner runs forever.
 *
 * Overridable per deployment with REACT_APP_LOADING_TIMEOUT_SECONDS in .env.
 * That name must keep the REACT_APP_ prefix (Create React App only inlines
 * variables named that way into the browser bundle) and is read at BUILD
 * time, so a change needs a dev-server restart or a rebuild to take effect.
 */
export const DEFAULT_LOADING_TIMEOUT_SECONDS = 10;

/**
 * That limit in milliseconds — the single value every loading state is
 * measured against.
 *
 * A missing, blank, non-numeric or zero/negative value falls back to the
 * default rather than being used as-is: setTimeout treats NaN as 0, which
 * would fire the error screen instantly on every load. Any other number is
 * honoured exactly as configured.
 */
export const LOADING_TIMEOUT_MS = (() => {
  const configured = Number(process.env.REACT_APP_LOADING_TIMEOUT_SECONDS);
  const seconds = Number.isFinite(configured) && configured > 0
    ? configured
    : DEFAULT_LOADING_TIMEOUT_SECONDS;
  return seconds * 1000;
})();

// ---- Connection-error screen (components/ConnectionErrorScreen.jsx) ----
// Shown when a load times out or sign-in fails. Guests get the "Book your
// court" screen, whose one button hard-reloads straight into the booking
// grid; staff routes (admin, POS) get the apology copy and a plain Refresh
// that reloads the screen they were on.

/** Guest headline — rendered uppercase by .jt-conn-error-title. */
export const BOOK_COURT_SCREEN_TITLE = 'Book your court';
/**
 * Guest message, in three parts so the middle can be set in bold:
 * "<lead><highlight><trail>".
 */
export const BOOK_COURT_SCREEN_MESSAGE_LEAD = 'We’re glad you’re considering us as your pickleball destination! Tap below to explore ';
export const BOOK_COURT_SCREEN_MESSAGE_HIGHLIGHT = 'the available time slots';
export const BOOK_COURT_SCREEN_MESSAGE_TRAIL = ' and find the perfect time for your next game.';
/** Smaller line under the guest message. */
export const BOOK_COURT_SCREEN_SUBTEXT = 'Your game is just a few taps away. Select a slot.';
/** The guest screen's button. */
export const BOOK_COURT_SCREEN_CTA_LABEL = 'View Available Slots';
/** Spinner caption while the hard reload into the booking grid runs. */
export const BOOK_COURT_SCREEN_LOADING_LABEL = 'Loading available slots';

/**
 * Where the guest screen's button lands: the root route with the `book=1`
 * signal, which App.jsx reads on first mount to open straight on the
 * time-slot grid instead of the landing page. The welcome splash plays on
 * that load as on every other, so the guest sees welcome -> time slots.
 */
export const BOOKING_GRID_URL = `${process.env.PUBLIC_URL || ''}/?book=1`;

/** Staff-route headline and message. */
export const CONNECTION_ERROR_TITLE = "We're sorry!";
export const CONNECTION_ERROR_MESSAGE = 'It looks like there is an issue with your internet connection or the website has encountered a slight issue. We’d appreciate if you can click the Refresh button below or refresh the page.';
// The staff-route button; the message above refers to it by this name.
export const CONNECTION_ERROR_RETRY_LABEL = 'Refresh';
// Spinner caption while a staff-route hard refresh clears caches and reloads.
export const CONNECTION_ERROR_REFRESHING_LABEL = 'Refreshing';

/**
 * Cap on the cache-bypassing re-fetch a hard refresh makes before reloading
 * (utils/hardRefreshUtil.js). This screen is usually showing because the
 * connection is bad, and an un-capped fetch on a dead connection can hang for
 * minutes — so after this long the page reloads anyway.
 */
export const HARD_REFRESH_FETCH_TIMEOUT_MS = 5000;

// ============================================================
// Checkout in its own browser tab
// ============================================================
// The payment step (CheckoutSummary) opens in a separate tab instead of a
// modal over the booking screen. The two tabs share one "checkout session",
// passed over a BroadcastChannel (or localStorage events where that API is
// missing) — see utils/checkoutSessionUtil.js and hooks/useCheckoutTab.js.

// Route the checkout tab is served on; the session id is appended as a path
// segment (/checkout/<sessionId>).
export const CHECKOUT_ROUTE_PATH = '/checkout';

// Same, for the Open Play join checkout (OpenPlayCheckoutPage).
export const OPEN_PLAY_CHECKOUT_ROUTE_PATH = '/open-play-checkout';

// What a checkout tab is showing (hooks/useCheckoutPageSession.js).
//   PROBING  - no stored session; asking the opening tab for it
//   WAITING  - the opening tab is still writing the record
//   ACTIVE   - checkout form
//   WORKING  - releasing / discarding after cancel or timeout
//   ENDED    - checkout finished but the browser refused to close the tab
//   INACTIVE - nothing to show (old link, or the opening tab never answered)
export const CHECKOUT_PAGE_PHASES = {
  PROBING: 'probing',
  WAITING: 'waiting',
  ACTIVE: 'active',
  WORKING: 'working',
  ENDED: 'ended',
  INACTIVE: 'inactive',
};

// Step headings on the payment part of both checkout pages (court booking
// and Open Play), styled like the reference number (.cs-step-label).
export const PAYMENT_STEP_LABELS = {
  PAY: 'STEP 1: SCAN THE QR CODE TO PAY',
  UPLOAD: 'STEP 2: UPLOAD RECEIPT AND SUBMIT',
};

// The payment QR's own controls on both checkout pages (PaymentQrDisplay):
// the small button under the QR, the full-screen viewer's button, and the
// name a saved QR is given ("payment-qr-gcash.png").
export const PAYMENT_QR_LABELS = {
  DOWNLOAD: 'Download QR',
  DOWNLOADING: 'Saving…',
  VIEW_HINT: 'Tap the QR to view it full screen',
  DOWNLOAD_FAILED: 'The QR could not be saved. Press and hold the image to save it instead.',
};
export const PAYMENT_QR_FILE_PREFIX = 'payment-qr';

// BroadcastChannel name prefix; each session gets its own channel.
export const CHECKOUT_CHANNEL_NAME = 'courtbooking-checkout';

// localStorage key prefix for a session's booking payload.
export const CHECKOUT_STORAGE_KEY_PREFIX = 'courtbooking:checkout:';

// Sessions nobody cleaned up (tab crashed, browser closed) are pruned from
// localStorage after this long.
export const CHECKOUT_SESSION_MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Stored session states. PREPARING is written the moment the tab is opened,
// before the booking record exists; READY once it does.
export const CHECKOUT_SESSION_STATES = {
  PREPARING: 'preparing',
  READY: 'ready',
};

// Messages between the two tabs.
//   booking tab -> checkout tab: SESSION, CLOSE
//   checkout tab -> booking tab: REQUEST_SESSION, RECEIPT_UPLOADED,
//                                SUBMITTED, CANCELLED, EXPIRED
export const CHECKOUT_EVENTS = {
  REQUEST_SESSION: 'request_session',
  SESSION: 'session',
  CLOSE: 'close',
  RECEIPT_UPLOADED: 'receipt_uploaded',
  SUBMITTED: 'submitted',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired',
};

// Which side sent a message — each tab ignores its own.
export const CHECKOUT_MESSAGE_SOURCES = {
  BOOKING: 'booking',
  CHECKOUT: 'checkout',
};

// What the booking tab knows about the checkout tab.
//   IDLE      - no checkout in progress
//   PREPARING - tab opened, booking record still being written
//   OPEN      - tab open with the booking loaded
//   BLOCKED   - the browser refused to open the tab (popup blocker)
//   CLOSED    - the guest closed the tab without finishing
export const CHECKOUT_TAB_STATES = {
  IDLE: 'idle',
  PREPARING: 'preparing',
  OPEN: 'open',
  BLOCKED: 'blocked',
  CLOSED: 'closed',
};

// How often the booking tab checks whether the checkout tab was closed.
export const CHECKOUT_TAB_POLL_MS = 1000;

// How long a freshly opened checkout tab waits for the booking tab to hand
// over the booking. Booking creation runs Firebase transactions per date,
// so this is generous.
export const CHECKOUT_PREPARE_TIMEOUT_MS = 60 * 1000;

// How long a checkout tab with no stored session waits for the booking tab
// to answer before showing "no longer active".
export const CHECKOUT_PROBE_TIMEOUT_MS = 3000;

// Short pause before the checkout tab closes itself, so its last message is
// delivered first.
export const CHECKOUT_CLOSE_DELAY_MS = 150;

// Booking statuses that mean the guest never submitted the booking. Anything
// else (pending, confirmed, ...) means the receipt went in.
export const UNSUBMITTED_BOOKING_STATUSES = ['awaiting_payment', 'cancelled'];


/* ---- Client-side config cache (utils/configCacheUtil.js) ---------------- */

/**
 * IndexedDB database/store/key holding the cached club config.
 *
 * The config is read on EVERY page load by every visitor (ConfigContext) and
 * carries every uploaded image as base64, so re-downloading it per visit was
 * the single largest consumer of the Realtime Database's daily download quota.
 * Caching it locally and revalidating against `config.lastModified` turns a
 * repeat visit into a read of one number.
 */
export const CONFIG_CACHE_DB_NAME = 'courtbooking-cache';
export const CONFIG_CACHE_STORE_NAME = 'config';
export const CONFIG_CACHE_KEY = 'config';

/**
 * How long a cached config may be served before it is refetched regardless of
 * `lastModified`.
 *
 * The stamp is written by updateConfig(), so an admin editing /config straight
 * from the Firebase console bypasses it and every cached visitor would hold a
 * stale config indefinitely. This bounds that to a day at the cost of one full
 * read per visitor per day.
 */
export const CONFIG_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

/**
 * How long to wait for IndexedDB to open before giving up and running
 * uncached. Guards the app's boot path against an open that never settles
 * (another tab holding an older version open).
 */
export const CONFIG_CACHE_OPEN_TIMEOUT_MS = 3000;

/* ---- Payment receipts (utils/receiptUtil.js) --------------------------- */

/**
 * Firebase nodes holding payment receipt images, keyed by the id of the
 * booking or Open Play join they belong to.
 *
 * Kept out of the records themselves because a receipt is ~330KB of base64
 * and the records are read in bulk by screens that never display one — the
 * availability grids, the reservation queue, the export. The record carries a
 * `hasPaymentScreenshot` boolean instead, and the image is fetched only when
 * someone asks to see it.
 */
export const BOOKING_RECEIPTS_NODE = 'bookingReceipts';
export const OPEN_PLAY_RECEIPTS_NODE = 'openPlayReceipts';

/* ---- Error log (utils/errorLogUtil.js) --------------------------------- */

/**
 * Firebase node holding captured runtime errors. Append-only, same shape of
 * contract as `auditLog` — see database.rules.json.
 */
export const ERROR_LOG_NODE = 'errorLogs';

/**
 * Where an entry came from. The source matters when reading the log back: an
 * `uncaught` entry broke a render, while a `console_error` entry is code that
 * caught the failure and carried on, and the two should never be triaged the
 * same way.
 */
export const ERROR_LOG_SOURCES = {
  UNCAUGHT: 'uncaught',
  UNHANDLED_REJECTION: 'unhandled_rejection',
  CONSOLE_ERROR: 'console_error',
  CONSOLE_WARN: 'console_warn',
  RESOURCE: 'resource',
  // Written by an explicit logAppError() call inside a catch block, so it
  // carries real context (db path, operation, record id) rather than a
  // message string scraped off the console.
  MANUAL: 'manual',
};

export const ERROR_LOG_SOURCE_LABELS = {
  [ERROR_LOG_SOURCES.UNCAUGHT]: 'Uncaught error',
  [ERROR_LOG_SOURCES.UNHANDLED_REJECTION]: 'Unhandled promise rejection',
  [ERROR_LOG_SOURCES.CONSOLE_ERROR]: 'console.error',
  [ERROR_LOG_SOURCES.CONSOLE_WARN]: 'console.warn',
  [ERROR_LOG_SOURCES.RESOURCE]: 'Failed resource load',
  [ERROR_LOG_SOURCES.MANUAL]: 'Reported by app code',
};

export const ERROR_LOG_LEVELS = {
  ERROR: 'error',
  WARN: 'warn',
};

/**
 * Days shown when the Error Logs screen first opens. Deliberately short for
 * the same reason as DEFAULT_AUDIT_DAYS: opening the screen must never pull
 * the whole node.
 */
export const DEFAULT_ERROR_LOG_DAYS = 3;

/**
 * Entries older than this are offered for deletion on the admin screen. The
 * log is a triage tool, not a permanent record — nothing downstream reads it,
 * and an unbounded node is the one way this feature could turn into a real
 * storage cost.
 */
export const ERROR_LOG_RETENTION_DAYS = 30;

/* ---- Write guards -------------------------------------------------------
 * A logger with no ceiling is a liability, not a diagnostic. A component that
 * throws on every render, or a failing listener the SDK retries, can produce
 * thousands of identical errors a minute; without these three limits the
 * first such bug would write far more to the database than the bug itself
 * ever cost. Every one of them is per browser tab, so a single misbehaving
 * client cannot poison the log for everyone else.
 * ---------------------------------------------------------------------- */

/**
 * Identical errors (same source + message + first stack frame) inside this
 * window collapse into ONE record. The repeats are counted and reported on
 * the next record for that fingerprint via `repeatCount`, so nothing is
 * hidden — an error firing 400 times still reads as 400, in one row.
 */
export const ERROR_LOG_DEDUPE_WINDOW_MS = 60 * 1000;

/** Hard ceiling on records written per tab, per page load. */
export const ERROR_LOG_MAX_PER_SESSION = 100;

/** Hard ceiling on records written per tab, per minute. */
export const ERROR_LOG_MAX_PER_MINUTE = 20;

/**
 * Queued entries are flushed together on this interval as ONE multi-path
 * update, rather than a round trip per error. A burst of 20 errors is a
 * single write.
 */
export const ERROR_LOG_FLUSH_INTERVAL_MS = 5000;

/* ---- Field caps ----------------------------------------------------------
 * Mirrored by .validate rules in database.rules.json. A stack trace is the
 * only genuinely large field, and 4000 characters is roughly 30 frames —
 * past that the tail is framework internals nobody reads.
 * ---------------------------------------------------------------------- */
export const ERROR_LOG_MAX_MESSAGE_LEN = 1000;
export const ERROR_LOG_MAX_STACK_LEN = 4000;
export const ERROR_LOG_MAX_CONTEXT_LEN = 1000;

/**
 * Console output matching any of these is NOT logged. They are either noise
 * the app prints about itself on every boot, or — in the case of the logger's
 * own failures — the start of an infinite loop: a failed write to /errorLogs
 * prints an error, which would be captured, which would be written, which
 * would fail again.
 */
export const ERROR_LOG_IGNORE_PATTERNS = [
  '[errorLog]',
  // The logger reporting its OWN failure. When a write to /errorLogs is
  // rejected, the Firebase SDK prints
  //   FIREBASE WARNING: update at /errorLogs failed: permission_denied
  // which matches ERROR_LOG_WARN_ALLOW_PATTERNS below and would be captured as
  // a brand new error — so a missing rule fills the log and the failure email
  // with PERMISSION_DENIED entries about the logger, burying the real errors
  // that prompted them. The dedupe window and the session cap stop it running
  // away, but the noise is still worthless: that the write failed is already
  // said once, in the report's Reason line.
  '/errorLogs',
  'ResizeObserver loop',
  'Download the React DevTools',
  '[App Check] dev mode',
];

/**
 * console.warn is noisy by nature, so warnings are logged ONLY when they look
 * like a Firebase failure. This is not an optimisation: the Realtime Database
 * SDK reports a rejected read as `FIREBASE WARNING: permission_denied`
 * through console.warn and never throws, so without this list the single most
 * important class of production failure would be missed entirely.
 */
export const ERROR_LOG_WARN_ALLOW_PATTERNS = [
  'FIREBASE WARNING',
  'permission_denied',
  'PERMISSION_DENIED',
  '@firebase',
  'App Check',
];

/* ---- Error log: surviving an unauthenticated failure --------------------
 * The whole node is gated on `auth != null`, which leaves one hole that
 * matters more than everything else the logger catches: if the failure IS
 * authentication — the silent sign-in never resolved, Auth is unreachable,
 * App Check rejected the client — then there is no one to write as, and the
 * error that explains the outage is exactly the one that cannot be recorded.
 *
 * The answer is NOT to open the node to unauthenticated writes. This app
 * gates every node on `auth != null`, App Check is still Unenforced, and a
 * world-writable node is a standing invitation to fill the database.
 *
 * Instead an entry that cannot be sent yet is held in localStorage and sent
 * later — when sign-in lands a moment afterwards (the common case: the error
 * beat the anonymous sign-in), or on the visitor's next page load. The caps
 * below bound what that buffer can cost a device.
 * ---------------------------------------------------------------------- */

/** localStorage key holding entries captured but not yet written. */
export const ERROR_LOG_BUFFER_KEY = 'cbs.errorLogBuffer';

/** Oldest buffered entries are dropped past this many. */
export const ERROR_LOG_BUFFER_MAX_ENTRIES = 60;

/**
 * A buffered entry this old is discarded unsent. A week is long enough to
 * cover a visitor who hits a broken deployment on Friday and returns Monday,
 * and short enough that nothing stale arrives looking current.
 */
export const ERROR_LOG_BUFFER_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Consecutive failed flushes before this tab gives up for the rest of the
 * page load. The entries are NOT lost — they stay buffered for the next one.
 * Anything lower than this turns the ordinary boot race into a dead logger.
 */
export const ERROR_LOG_MAX_FLUSH_ATTEMPTS = 4;

/** First retry delay; doubles per attempt. */
export const ERROR_LOG_RETRY_BASE_MS = 3000;


/* ---- Error log: the backend sinks (utils/errorLogRemoteUtil.js) ---------
 * Two further destinations for the same captured errors, both reached through
 * courtbooking-be:
 *
 *   2. Vercel runtime logs — every entry. A live window while you are
 *      debugging something, not an archive: retention is 1 hour on Vercel's
 *      Hobby plan.
 *   3. Email — only when the Realtime Database write failed or was never
 *      possible. Batched and rate-limited on the backend.
 *
 * They exist because the database sink cannot record a failure to
 * authenticate: its rules need an identity, and a broken sign-in has none.
 * This path touches no Firebase service, so it still works when the app never
 * managed to initialise at all.
 * ---------------------------------------------------------------------- */

/**
 * Route appended to the backend's ORIGIN. The origin is taken from
 * REACT_APP_EMAIL_ENDPOINT_BASE_URL, which every client .env already sets, so
 * no client environment file had to change to enable this.
 */
export const ERROR_LOG_REMOTE_PATH = '/api/error-log';

/**
 * How long to let one report hang before aborting. Short on purpose: this is
 * a background request made while something is already going wrong, and a
 * slow backend must not hold a connection open for the life of the page.
 */
export const ERROR_LOG_REMOTE_TIMEOUT_MS = 8000;

/**
 * What happened to the database sink, reported alongside every entry. Only
 * FAILED and UNAVAILABLE cause the backend to send an email — PENDING means
 * the write is still in flight, which is not a failure.
 */
export const ERROR_LOG_RTDB_STATUS = {
  SAVED: 'saved',
  PENDING: 'pending',
  FAILED: 'failed',
  UNAVAILABLE: 'unavailable',
};

/**
 * How long to wait for a sign-in before treating "nobody is authenticated" as
 * a permanent failure and emailing the report.
 *
 * Without this the most important case falls through the cracks. The database
 * rules need an identity, so flush() holds entries when there is no signed-in
 * user and waits for the auth callback — which is right for the ordinary boot
 * race, where sign-in lands a second later. But if authentication is what
 * broke, that callback never fires: nothing retries, nothing fails, and the
 * email that should announce the outage is never sent. The entries simply sit
 * in localStorage forever.
 *
 * 20 seconds is far longer than a healthy sign-in takes and short enough to
 * still be useful as an alert.
 */
export const ERROR_LOG_AUTH_GRACE_MS = 20000;

/**
 * User-agent markers of social-media in-app browsers (Facebook, Messenger,
 * Instagram, TikTok, LINE, X/Twitter, LinkedIn, Snapchat, Pinterest, Viber,
 * WeChat, Threads). Links shared on these apps open inside a stripped-down
 * webview where new tabs (checkout), downloads and some payment flows break,
 * so the app nudges guests to reopen the page in their real browser.
 */
export const IN_APP_BROWSER_UA_PATTERN = /FBAN|FBAV|FB_IAB|FBIOS|FB4A|Messenger|Instagram|Barcelona|musical_ly|BytedanceWebview|TikTok|\bLine\/|Twitter|LinkedInApp|Snapchat|Pinterest|Viber|MicroMessenger/i;

/** Message shown on the "open in your browser" banner (see InAppBrowserBanner). */
export const IN_APP_BROWSER_BANNER_TEXT = 'For the best experience, ';
export const IN_APP_BROWSER_BANNER_LINK_TEXT = 'open in browser';

/**
 * User-agent markers of Facebook's own in-app browser (the Facebook app and
 * Messenger). Narrower than IN_APP_BROWSER_UA_PATTERN on purpose: the popup
 * and error-screen copy below name Facebook, so they only show there.
 */
export const FACEBOOK_IN_APP_UA_PATTERN = /FBAN|FBAV|FB_IAB|FBIOS|FB4A|FBDV|Messenger/i;

/**
 * Query parameters Facebook appends to shared links. Stripped before the page
 * is handed to the real browser so the guest lands on the site's normal URL,
 * not a long tracking link.
 */
export const FACEBOOK_TRACKING_PARAMS = ['fbclid', 'mibextid'];

/** Shared copy for the Facebook-browser popup and the Facebook error screen. */
export const FACEBOOK_BROWSER_MESSAGE = 'Facebook’s in-app browser may affect some booking features. Open this page in your phone\'s default browser for a smoother booking experience. (e.g. Safari, Chrome, Edge)';
export const FACEBOOK_BROWSER_MODAL_TITLE = 'Open in Your Browser';
/**
 * The tinted help card on the popup: a way into the manual-steps guide, and
 * the page link to copy. The link text sits between the two halves.
 */
export const FACEBOOK_BROWSER_HELP_TITLE = 'Button not working?';
export const FACEBOOK_BROWSER_HELP_TEXT_BEFORE = 'No worries — ';
export const FACEBOOK_BROWSER_HELP_LINK = 'see how to open it in two taps';
export const FACEBOOK_BROWSER_HELP_TEXT_AFTER = ', or copy the link below and paste it into Safari or Chrome.';
export const FACEBOOK_BROWSER_OPEN_LABEL = 'Open in Browser';
export const FACEBOOK_BROWSER_TRY_AGAIN_LABEL = 'Try Again';

/** Wait after the page loads before the "Open in your browser" popup appears (FacebookBrowserModal). */
export const FACEBOOK_BROWSER_MODAL_DELAY_MS = 3000;

/**
 * How long "Open in Browser" gives the device to take the page away before
 * the manual-steps guide pops up (see watchExternalBrowserHandoff).
 */
export const EXTERNAL_BROWSER_HANDOFF_TIMEOUT_MS = 2500;

/** Messenger's marker within FACEBOOK_IN_APP_UA_PATTERN (FBAN/MessengerForiOS, FB_IAB/MESSENGER). */
export const MESSENGER_IN_APP_UA_PATTERN = /Messenger/i;

/** App names shown in the guide's picture of the in-app browser's top bar. */
export const IN_APP_BROWSER_APP_NAMES = {
  FACEBOOK: 'Facebook',
  MESSENGER: 'Messenger',
};

/** Copy Link field on the popup's help card (components/CopyLinkField.jsx). */
export const COPY_LINK_LABEL = 'Copy';
export const COPY_LINK_COPIED_LABEL = 'Copied!';
export const COPY_LINK_FAILED_LABEL = 'Hold link to copy';
/** How long the Copy button shows its copied/failed state before resetting. */
export const COPY_LINK_FEEDBACK_MS = 2500;

/**
 * "Open it manually" guide (components/ExternalBrowserGuideModal.jsx), shown
 * when "Open in Browser" could not hand the page over, or on request from the
 * help card. Each step has a picture of the part of the in-app browser to tap.
 */
export const EXTERNAL_BROWSER_GUIDE_TITLE = 'Open in Browser Manually';
export const EXTERNAL_BROWSER_GUIDE_INTRO = 'Your app didn’t let us switch automatically, but it only takes two taps:';
export const EXTERNAL_BROWSER_GUIDE_STEPS = {
  MENU: 'Tap the ••• button at the top-right corner of the screen.',
  OPEN: 'Choose “Open in external browser”.',
};
export const EXTERNAL_BROWSER_GUIDE_TAP_LABEL = 'Tap';
export const EXTERNAL_BROWSER_GUIDE_DONE_LABEL = 'Got it';
/**
 * Rows drawn in the guide's picture of the ••• menu. Only the first is the
 * one to tap; the others are there so the picture reads as that menu.
 */
export const EXTERNAL_BROWSER_GUIDE_MENU_ITEMS = ['Open in external browser', 'Copy link', 'Refresh'];

/**
 * UI preview page (components/BrowserUiPreviewPage.jsx): shows the in-app
 * browser popup, the manual-steps guide and its pictures in any browser, so
 * they can be checked without opening the site from Facebook.
 */
export const UI_PREVIEW_ROUTE_PATH = '/components/browser';
export const UI_PREVIEW_TITLE = 'In-App Browser UI Preview';
export const UI_PREVIEW_INTRO = 'What guests see when they open the site from Facebook or Messenger. Nothing here leaves this page.';
export const UI_PREVIEW_APP_HEADING = 'App';
export const UI_PREVIEW_POPUPS_HEADING = 'Popups';
export const UI_PREVIEW_SHOW_POPUP_V1_LABEL = 'Show Version 1 popup (default)';
export const UI_PREVIEW_SHOW_POPUP_LABEL = 'Show Version 2 popup';
export const UI_PREVIEW_SHOW_GUIDE_LABEL = 'Show manual-steps guide';
export const UI_PREVIEW_POPUP_NOTE = 'Pick the version guests see under Config → Theme → Open in Browser Popup. Here, “Open in Browser” never leaves the page: Version 1 just closes, and Version 2 acts as if the hand-off failed and opens the guide. Version 2 also gets a × here that guests don’t.';
export const UI_PREVIEW_PICTURES_HEADING = 'Guide pictures';

/* ---- Redeploy (utils/redeployUtil.js, components/PendingChangesBanner.jsx) ----
 *
 * A config change only reaches visitors when the site is rebuilt, because
 * scripts/snapshotConfig.js bakes the landing page's share of /config into the
 * build. These drive the banner that says so and the button that fixes it.
 */

/**
 * Where the redeploy endpoint lives, appended to REACT_APP_EMAIL_ENDPOINT_BASE_URL.
 * Same backend as the booking emails, so there is no second URL to configure.
 */
export const REDEPLOY_ENDPOINT_PATH = '/redeploy';

/**
 * Where the Redeploy client workflow records its last successful run. Its own
 * node rather than a field on /config: writing into /config from CI risks
 * clobbering a settings object guests also write, and every visitor downloads
 * /config on first load.
 */
export const DEPLOY_STATUS_PATH = 'deployStatus';

/**
 * entityId for the baseline audit entry. /deployStatus is one fixed node,
 * not a record with a key of its own, so the trail needs a stable literal
 * here the way a run id serves the workflow's entries.
 */
export const DEPLOY_BASELINE_ENTITY_ID = 'baseline';

/**
 * lastStatus recorded by a seeded baseline, to tell it apart from a real
 * deploy's 'success' when reading the node or the audit trail later.
 */
export const DEPLOY_STATUS_BASELINE = 'baseline';

/**
 * The other values deployStatus.lastStatus can hold. 'running' is written by
 * the Redeploy client workflow when it starts and replaced when it finishes,
 * which is what lets the banner show a run in progress to every admin, on
 * every device, rather than only in the tab that pressed the button.
 */
export const DEPLOY_STATUS_RUNNING = 'running';
export const DEPLOY_STATUS_SUCCESS = 'success';
export const DEPLOY_STATUS_FAILED = 'failed';

/**
 * How long a run may claim to be running before the banner stops believing it.
 *
 * A workflow cancelled from the Actions tab, or one that dies before its
 * failure step, never clears its own marker. Without a ceiling the button
 * would stay disabled forever and the only way out would be the Firebase
 * console. Generous against REDEPLOY_TYPICAL_MINUTES on purpose: a queued
 * run on a busy morning is slow, not stuck.
 */
export const REDEPLOY_RUN_STALE_MS = 20 * 60 * 1000;

/**
 * How long a redeploy usually takes, for the wording on the banner. Measured
 * rather than guessed: install, build and release on a GitHub runner.
 */
export const REDEPLOY_TYPICAL_MINUTES = '3-5';

/**
 * Grace period between a config save and the banner appearing.
 *
 * Without it the banner pops up the instant an admin saves, while they are
 * still editing, and nags through a run of small changes. The sensible moment
 * to redeploy is after the editing is done, not between keystrokes.
 */
export const REDEPLOY_BANNER_DELAY_MS = 60 * 1000;

/**
 * How long the banner keeps polling deployStatus after a redeploy is started,
 * and how often. The run takes minutes, so this is a slow poll that gives up
 * rather than a progress bar - the audit trail and the GitHub run are the
 * authoritative record either way.
 */
export const REDEPLOY_POLL_INTERVAL_MS = 15 * 1000;
export const REDEPLOY_POLL_TIMEOUT_MS = 10 * 60 * 1000;

/* ---- Time slot button colors (Config -> Theme) ----
 *
 * Per-status background and text colours for the guest booking grid's slot
 * buttons (TimeSlotSelection). Stored in config under
 * SLOT_BUTTON_COLORS_CONFIG_KEY as { [status]: { background, text } }, holding
 * ONLY the colours an admin changed — an empty map is "all defaults", which
 * is exactly what Reset writes. See utils/slotButtonColorUtil.js.
 */

/** Config key the slot button colours are saved under. Also in the build snapshot (scripts/snapshotConfig.js). */
export const SLOT_BUTTON_COLORS_CONFIG_KEY = 'slotButtonColors';

/**
 * Sentinel default meaning "follow Config -> Theme -> Active Button Color",
 * so a club that never touches the Selected colour keeps it in step with its
 * accent the way it always has.
 */
export const SLOT_COLOR_FOLLOW_ACTIVE = 'active';
/** Checkbox label on Selected's background (Config -> Theme -> Time Slot Button Colors). */
export const SLOT_COLOR_FOLLOW_ACTIVE_LABEL = 'Use active button color';

/**
 * Every slot button state the guest grid draws, in the order the admin
 * editor lists them. `defaults` are what a club that never changes them gets;
 * TimeSlotSelection.css falls back to the same values, so keep the two in
 * step. `cssKey` names the CSS custom properties
 * (--jt-slot-<cssKey>-bg / -text / -border).
 */
export const SLOT_BUTTON_STATUSES = [
  { key: 'open', label: 'Open', sample: 'Open', cssKey: 'open', defaults: { background: '#c6dec6', text: '#2c5c4a' } },
  { key: 'selected', label: 'Selected', sample: 'Selected', cssKey: 'selected', defaults: { background: SLOT_COLOR_FOLLOW_ACTIVE, text: '#ffffff' } },
  { key: 'booked', label: 'Booked', sample: 'Booked', cssKey: 'booked', defaults: { background: '#98abc5', text: '#000000' } },
  { key: 'pending', label: 'Pending', sample: 'Pending', cssKey: 'pending', defaults: { background: '#fdecc8', text: '#b3730a' } },
  { key: 'passed', label: 'Passed', sample: 'Passed', cssKey: 'passed', defaults: { background: '#dfe1e6', text: '#7c7c7c' } },
  { key: 'unavailable', label: 'Closed / Unavailable', sample: 'Closed', cssKey: 'unavailable', defaults: { background: '#eceef0', text: '#98a0aa' } },
  { key: 'openPlay', label: 'Open Play', sample: 'Open Play', cssKey: 'openplay', defaults: { background: '#3d4faa', text: '#ffffff' } },
];

/** The two colour parts each slot state carries, in editor order. */
export const SLOT_COLOR_PARTS = [
  { key: 'background', label: 'Background', cssSuffix: 'bg' },
  { key: 'text', label: 'Font', cssSuffix: 'text' },
];

/**
 * How much darker than a custom background its border and hover shade are
 * (shadeHex percent). Matches the step between the original defaults, e.g.
 * booked #dfe1e6 -> border #c8ccd3.
 */
export const SLOT_COLOR_BORDER_SHADE = -9;
export const SLOT_COLOR_HOVER_SHADE = -8;
/** Second stripe of the Closed hatch, relative to its background. */
export const SLOT_COLOR_HATCH_SHADE = -3;

// ============================================================
// Routes and welcome splash
// ============================================================

/** Admin dashboard route (App.jsx). */
export const ADMIN_ROUTE_PATH = '/admin-screen';

/** Point of Sale route (App.jsx). */
export const POS_ROUTE_PATH = '/pos';

/**
 * Site theme colours used when nothing else is configured. ConfigContext's
 * DEFAULT_CONFIG and the build-time branding (utils/buildBrandingUtil.js)
 * both read these, so a build with no config snapshot paints the same
 * palette everywhere.
 */
export const DEFAULT_THEME_COLORS = {
  text: '#3d1220',
  background: '#f1efe9',
  headerBackground: '#ffffff',
  active: '#6fb5ab',
  brand: '#3d1220',
};

// ---- Booking reference PDF (utils/pdfReceipt.js) ----

/** Title of the disclaimer callout printed under the booking total. */
export const BOOKING_REFERENCE_NOTE_TITLE = 'Important note';

/** Disclaimer body: the PDF is a summary, not a receipt or a confirmation. */
export const BOOKING_REFERENCE_NOTE_MESSAGE = 'This Reservation Details summary is not an official receipt nor confirms your reservation. This is only a booking details summary submitted by you - admin approval is still needed to confirm this reservation.';

/** Amber callout palette, matching the site's "Please note" box. */
export const BOOKING_REFERENCE_NOTE_COLORS = {
  background: '#fcebc8',
  border: '#f0b86b',
  text: '#a87b2c',
};

/**
 * How long the welcome (components/FirstVisitSplash.jsx) stays up on each page
 * load, in seconds, when .env.common does not set
 * REACT_APP_WELCOME_SCREEN_SECONDS. It does NOT wait for App Check, sign-in or
 * any data — those keep running behind it, and whatever is still loading when
 * it ends carries on in the first screen.
 */
export const DEFAULT_WELCOME_SCREEN_SECONDS = 5;
// The floor leaves room for the animation to land (logo, then greeting by
// ~1.8s) before the closing wipe; the ceiling keeps a typo in .env.common
// from parking every visitor on the welcome.
export const MIN_WELCOME_SCREEN_SECONDS = 2;
export const MAX_WELCOME_SCREEN_SECONDS = 15;

/**
 * The final part of that duration spent on the closing wipe — the welcome
 * retracting diagonally from the bottom-right corner to the top-left,
 * mirroring the wipe it opens with — so the hand-off is not a hard cut.
 */
export const FIRST_VISIT_SPLASH_EXIT_MS = 700;

/**
 * The welcome's court drawing and club name are a tone of the header
 * background (colorUtil.tonalCompanion): mixed this far toward white on a dark
 * header or toward black on a light one, then kept at least this contrast
 * ratio against the header so they always read.
 */
export const FIRST_VISIT_SPLASH_TONE_SHIFT = 0.55;
export const FIRST_VISIT_SPLASH_TONE_MIN_CONTRAST = 3;

/** Routes that never get the welcome: only the Point of Sale. Guest pages and the admin dashboard get it. */
export const FIRST_VISIT_SPLASH_EXCLUDED_PATHS = [POS_ROUTE_PATH];

/** Line above the club name on the splash ("Welcome to" / <club name>). */
export const FIRST_VISIT_SPLASH_GREETING = 'Welcome to';

/** The splash's whole greeting when the build has no club name to follow it. */
export const FIRST_VISIT_SPLASH_GREETING_FALLBACK = 'Welcome!';

/** Credit line under the greeting — the same tagline the availability image carries. */
export const FIRST_VISIT_SPLASH_POWERED_BY = AVAILABILITY_IMAGE_TAGLINE;

/* ---- UI design versions (Config -> Theme -> UI Design) ----
 *
 * Which design the guest booking screen renders. Version 1 is the original
 * screen (TimeSlotSelection + the plain photo collage in App.jsx); Version 2
 * is the framed collage + Court Rates table design from index3.html
 * (BookingIntroV2 + TimeSlotSelectionV2). Stored under
 * UI_DESIGN_CONFIG_KEY and baked into the build snapshot, so a change reaches
 * guests on the next redeploy like every other guest-facing setting.
 */

/** Config key the selected design is saved under. Also in the build snapshot (scripts/snapshotConfig.js). */
export const UI_DESIGN_CONFIG_KEY = 'uiDesignVersion';

/** Stored values for each design. */
export const UI_DESIGN_VERSIONS = {
  V1: 'v1',
  V2: 'v2',
};

/** What a club that has never picked a design gets — the original screen. */
export const DEFAULT_UI_DESIGN_VERSION = UI_DESIGN_VERSIONS.V1;

/**
 * The dropdown's options, in order. `previewUrl` is a screenshot of the guest
 * booking screen in that design, served from public/ui-previews.
 */
export const UI_DESIGN_OPTIONS = [
  {
    value: UI_DESIGN_VERSIONS.V1,
    label: 'Version 1',
    description: 'The original booking screen: photo collage with the club name underneath, rates listed above the time slot grid.',
    previewUrl: '/ui-previews/booking-v1.webp',
  },
  {
    value: UI_DESIGN_VERSIONS.V2,
    label: 'Version 2',
    description: 'Framed photo collage with a club info panel, a compact Court Rates table, and a refreshed time slot grid.',
    previewUrl: '/ui-previews/booking-v2.webp',
  },
];

/* ---- Open in Browser popup versions (Config -> Theme -> Open in Browser Popup) ----
 *
 * Which popup guests see when they open the site inside Facebook's or
 * Messenger's in-app browser. Version 1 is the original closable popup
 * (FacebookBrowserModalV1); Version 2 is the illustrated, non-closable popup
 * with the manual-steps guide (FacebookBrowserModal). Stored under
 * BROWSER_PROMPT_CONFIG_KEY and baked into the build snapshot, so a change
 * reaches guests on the next redeploy like the UI design.
 */

/** Config key the selected popup is saved under. Also in the build snapshot (scripts/snapshotConfig.js). */
export const BROWSER_PROMPT_CONFIG_KEY = 'browserPromptVersion';

/** Stored values for each popup. */
export const BROWSER_PROMPT_VERSIONS = {
  V1: 'v1',
  V2: 'v2',
};

/** What a club that has never picked a popup gets — the original one. */
export const DEFAULT_BROWSER_PROMPT_VERSION = BROWSER_PROMPT_VERSIONS.V1;

/** The dropdown's options, in order. */
export const BROWSER_PROMPT_OPTIONS = [
  {
    value: BROWSER_PROMPT_VERSIONS.V1,
    label: 'Version 1',
    description: 'The original popup: a short message, an Open in Browser button and a "Keep using Facebook browser" option. Guests can close it.',
  },
  {
    value: BROWSER_PROMPT_VERSIONS.V2,
    label: 'Version 2',
    description: 'Illustrated popup with a copy-link card. If Open in Browser does not work, a step-by-step guide pops up. Guests cannot close it.',
  },
];

/** Config -> Theme section copy (components/BrowserPromptSettings.jsx). */
export const BROWSER_PROMPT_SETTINGS_TITLE = 'Open in Browser Popup';
export const BROWSER_PROMPT_SETTINGS_DESC = 'The popup guests see when they open the site from Facebook or Messenger. Changes apply once you press "Save Configuration" and redeploy.';
export const BROWSER_PROMPT_PREVIEW_LABEL = 'Preview popup';
/** Name the change is logged under in the audit trail. */
export const BROWSER_PROMPT_AUDIT_SETTING_NAME = 'Open in browser popup';

/** Version 1 popup copy (components/FacebookBrowserModalV1.jsx). */
export const FACEBOOK_BROWSER_V1_TITLE = 'Open in your browser';
export const FACEBOOK_BROWSER_V1_MESSAGE = 'For better experience, open the link in your browser. Some features of the website may not work with Facebook’s built-in web viewer.';
export const FACEBOOK_BROWSER_V1_STAY_LABEL = 'Keep using Facebook browser';

/**
 * Google Fonts stylesheet for Geist, the label face Version 2 is set in.
 * Loaded only while Version 2 is on screen (see hooks/useStylesheet.js), so
 * Version 1 keeps rendering exactly as it did.
 */
export const GEIST_FONT_STYLESHEET_URL = 'https://fonts.googleapis.com/css2?family=Geist:wght@400;500;600;700;800&display=swap';

/**
 * Outline of the slanted main photo in the Version 2 collage
 * (BookingGalleryCollage), as SVG path data in a 0-100 box that is stretched
 * over the photo. One shape drives both the photo's mask and the border drawn
 * along its edge, so the two can't drift apart.
 *   wide   - tablet and desktop: left edge from 18% in at the top to 0 at the bottom
 *   narrow - phones, where the photo spans the whole top row: 51% in at the top
 *            to 34% at the bottom, leaning into the club info
 */
export const COLLAGE_MAIN_PHOTO_SHAPES = {
  wide: 'M22 0 L96 0 Q100 0 100 4 L100 96 Q100 100 96 100 L4 100 Q0 100 0.7 96 L17.3 4 Q18 0 22 0 Z',
  narrow: 'M51 0 L95 0 Q100 0 100 6 L100 94 Q100 100 95 100 L39 100 Q34 100 34.8 94 L45.2 6 Q46 0 51 0 Z',
};

/**
 * Smallest font size (px) the Version 2 collage's club name shrinks to so each
 * part of it ("Whitehouse" / "Pickcoolbowl") stays on one line. A name too long
 * even at this size wraps between words instead of shrinking further.
 */
export const COLLAGE_TITLE_MIN_FONT_PX = 12;

/** Shown on a date chip past the advance-booking window (Version 2). `{days}` is the window length. */
export const BOOKING_WINDOW_LOCKED_HINT = 'Bookings open up to {days} days in advance.';

/* ---- Court rules before checkout (components/CourtRulesSheet.jsx) ----
 *
 * Config -> Court Reminders. With "Show court rules before checkout" on,
 * Continue on the guest details card shows the club's reminders text in a
 * popup the guest must scroll through and accept before checkout opens.
 */

/** Config key for the toggle. Off unless an admin turns it on. */
export const SHOW_COURT_RULES_CONFIG_KEY = 'showCourtRulesBeforeCheckout';
/**
 * Config key for the reminders text shown in the popup, written by the admin
 * as free text (line breaks, bullets and numbering are kept as typed — see
 * FormattedText). Empty means there is nothing to show, so the popup is
 * skipped even with the toggle on.
 */
export const COURT_REMINDERS_TEXT_CONFIG_KEY = 'courtRemindersText';
/** Longest reminders text the Config field accepts. */
export const COURT_REMINDERS_TEXT_MAX_LENGTH = 5000;
/** Config -> Court Reminders tab. */
export const COURT_REMINDERS_TAB_ID = 'courtReminders';
export const COURT_REMINDERS_TAB_LABEL = '📋 Court Reminders';
export const COURT_RULES_TITLE = 'Court Rules';
export const COURT_RULES_ACCEPT_LABEL = 'I Understand';
export const COURT_RULES_SCROLL_HINT = 'Scroll to the end to continue ↓';
/**
 * How close to the bottom (px) counts as "read to the end". A few pixels of
 * slack, because sub-pixel layout on phones can stop a scroll just short.
 */
export const COURT_RULES_SCROLL_END_TOLERANCE_PX = 8;

/* ---- Formatted free text (components/FormattedText.jsx) ---- */

/** Leading spaces that make one level of nesting (a tab counts as this many). */
export const FORMATTED_TEXT_INDENT_SPACES = 2;
/** Deepest nesting level rendered; deeper lines sit at this level. */
export const FORMATTED_TEXT_MAX_INDENT_LEVEL = 6;
/** Marker every "-", "*" or "•" bullet is shown with. */
export const FORMATTED_TEXT_BULLET = '•';

/* ---- Rate separators (Config -> Sports & Courts -> Hourly rates) ----
 *
 * Each hourly rate band can carry an `icon` and a `color`, used for the
 * separator row the guest time slot grid draws where that rate starts
 * (RateSeparatorPill). Both are optional: a band without them shows the
 * default pill in the club's accent colours, with no icon.
 */

/**
 * Emoji offered first in the icon picker (IconPickerModal) — the ones a rate
 * is most likely to be named after (day, night, peak, weekend) — before the
 * general sports and money marks.
 */
export const RATE_ICON_EMOJIS = [
  '\u2600\ufe0f', '\ud83c\udf19', '\ud83c\udf05', '\ud83c\udf07', '\ud83c\udf24\ufe0f', '\ud83c\udf03', '\u2b50', '\u2728',
  '\u26a1', '\ud83d\udd25', '\u2744\ufe0f', '\ud83c\udf27\ufe0f', '\ud83d\udd52', '\u23f0', '\ud83d\udcc5', '\ud83c\udf89',
  '\ud83d\udcb0', '\ud83d\udcb8', '\ud83c\udff7\ufe0f', '\ud83c\udf81', '\ud83c\udfbe', '\ud83c\udfd3', '\ud83c\udff8', '\ud83c\udfc6',
];

/**
 * Iconify's public API — free, no key, CORS-enabled, ~200k open-source icons
 * (the same source the Kawnter POS category icons use). Only the chosen id,
 * e.g. "mdi:weather-sunny", is ever saved; the SVG is fetched at display time.
 */
export const ICONIFY_SEARCH_URL = 'https://api.iconify.design/search';
export const ICONIFY_BASE_URL = 'https://api.iconify.design';
/** Results per icon search. Enough to choose from, few enough to scan. */
export const ICONIFY_SEARCH_LIMIT = 48;
/** How long typing must pause before an icon search goes out. */
export const ICON_SEARCH_DEBOUNCE_MS = 350;

/**
 * Longest icon value accepted. An emoji is a few bytes and real Iconify ids
 * are well under this; the cap only stops a malformed paste being saved.
 */
export const ICON_VALUE_MAX_LEN = 64;

/** Config key: whether the guest time slot grid shows rate separators. Absent counts as shown. */
export const RATE_SEPARATOR_SHOW_CONFIG_KEY = 'showRateSeparators';
/** Config key: which separator design the guest grid uses. */
export const RATE_SEPARATOR_DESIGN_CONFIG_KEY = 'rateSeparatorDesign';

/** Stored values for each separator design. */
export const RATE_SEPARATOR_DESIGNS = {
  PILL: 'design1',
  BAR: 'design2',
};

/** What a club that has never picked one gets. */
export const DEFAULT_RATE_SEPARATOR_DESIGN = RATE_SEPARATOR_DESIGNS.PILL;

/** The design dropdown's options (Config -> Sports & Courts -> Hourly rates). */
export const RATE_SEPARATOR_DESIGN_OPTIONS = [
  { value: RATE_SEPARATOR_DESIGNS.PILL, label: 'Design 1 \u2013 Pill' },
  { value: RATE_SEPARATOR_DESIGNS.BAR, label: 'Design 2 \u2013 Bar' },
];
